/*
  fp-ai-generator.js
  Upload -> modal -> generatie (live status) -> watermerk-preview + canvas-mockup.
  In de modal schuiven de PDP-koopblokken (variant-picker, prijs, ATC, upsell);
  die worden teruggezet bij sluiten. De <dialog> blijft een kind van
  <product-info>, zodat Dawn's variant-JS de nodes blijft vinden.

  Sinds v2 houdt de sessie een LIJST met artworks per scene bij (max 6). Eén
  daarvan is "actief": die staat in de modal, in de PDP-galerij en in de
  formulier-properties. Na add to cart flipt de kaart naar "maak er nog een".
*/
if (!customElements.get('fp-ai-generator')) {
  const FP_AI_MAX_ITEMS = 6;
  const FP_AI_STATE_VERSION = 2;
  // Wachtscherm. De balk loopt in FP_AI_ETA_MS naar FP_AI_CAP procent en kruipt
  // daarna asymptotisch verder, zodat een uitlopende generatie nooit stil lijkt
  // te staan. De blur zakt in stappen: elke stap is één re-raster in plaats van
  // een doorlopende filter-animatie (91% van het verkeer is mobiel).
  const FP_AI_ETA_MS = 30000;
  const FP_AI_CAP = 92;
  const FP_AI_TICK_MS = 250;
  const FP_AI_CRAWL_MS = 15000;
  const FP_AI_BLUR_STEPS = [30, 24, 18, 13, 9, 5];
  // Scene-modus blijft ook aan het eind duidelijk onscherp. Volledig scherp
  // verraadt dat de scene leeg is (bij mermaid zie je dan een lege oceaan) en
  // laat het echte resultaat minder hard landen. Bij 6px zie je nog wel wélke
  // wereld het is; vanaf ongeveer 8px blijft er alleen kleur over.
  const FP_AI_SCENE_BLUR_STEPS = [24, 20, 16, 12, 9, 6];
  // Metingen (docs/prompts/generatie-events-posthog.md). PostHog bestaat pas
  // ná cookie-consent en laadt async: nooit op wachten, nooit op crashen, en
  // geen eigen queue. Zonder PostHog is dit een stille no-op.
  const fpAiTrack = (name, props, options) => {
    try { window.posthog?.capture?.(name, props, options); } catch (e) { /* geen PostHog */ }
  };
  // Bijsturen met feedback: Engelse instructie per chip, zo naar het model
  // (niet vertaald). Alleen de chiplabels komen uit de locales.
  // Markering in de aanpassen-weergave: één persoon per aanpassing, ring = 16% van de beeldbreedte
  // (moet gelijk blijven aan de breedte van .fp-ai-gen__mark in de CSS).
  const FP_AI_MAX_MARKS = 1;
  const FP_AI_MARK_DIAMETER = 0.16;
  const FP_AI_REVISE_INSTRUCTIONS = {
    face: 'Make the face (features and proportions) look more like in the photo.',
    hair: 'Make the hair (colour, length and style) look more like in the photo.',
    older: 'They look too old: make them look a little younger, like in the photo.',
    younger: 'They look too young: make them look a little older, like in the photo.',
    more_smile: 'A little more smile.',
    less_smile: 'A little less smile, a more natural expression.',
    // Iemand die er niet in hoort (omstander, wie het kind vasthield). Alleen met
    // een aangetikte persoon en nooit samen met een andere knop.
    remove: 'Remove this person completely from the portrait and fill their place naturally with the scene, as if they were never there.',
  };
  const fpAiUuid = () =>
    (window.crypto?.randomUUID && crypto.randomUUID()) ||
    `fp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Naam op portret (custom.name_caption). Twee pure stappen, los van de
  // component zodat ze ook zonder Shopify te testen zijn. `api` is
  // window.FPCaption (fp-caption.js), dezelfde code die de server gebruikt om
  // de naam in de print te branden.
  //
  // Status: 'empty' (blokkeert nooit), 'pending' (tekst, maar fp-caption is
  // nog niet geladen), 'ok' of 'error' (reason: too_long | chars | blocked).
  // `trusted` = { raw, value } uit de sessie: bij het opslaan al goedgekeurd,
  // geldt alleen tot fp-caption geladen is en het opnieuw controleert.
  const fpAiCaptionEvaluate = (raw, api, trusted) => {
    const text = typeof raw === 'string' ? raw : '';
    if (!text.trim()) return { status: 'empty', value: null, reason: null };
    if (!api || typeof api.isReady !== 'function' || !api.isReady()) {
      if (trusted && trusted.value && trusted.raw === text) {
        return { status: 'ok', value: trusted.value, reason: null };
      }
      return { status: 'pending', value: null, reason: null };
    }
    const result = api.check(text);
    if (result.ok) return { status: 'ok', value: result.value, reason: null };
    if (result.reason === 'empty') return { status: 'empty', value: null, reason: null };
    return { status: 'error', value: null, reason: result.reason || 'chars' };
  };

  // Tekent de titel als SVG op de natuurlijke maat van het beeld in de overlay.
  // De overlay heeft via CSS exact de verhouding van het beeld, dus de SVG valt
  // precies over het zichtbare beeld. Zonder geldige naam, zonder geladen beeld
  // of zonder fp-caption: overlay leeg en verborgen.
  const fpAiCaptionPaint = (overlay, img, value, api) => {
    if (!overlay) return false;
    const width = img ? img.naturalWidth : 0;
    const height = img ? img.naturalHeight : 0;
    const clear = () => {
      overlay.textContent = '';
      overlay.hidden = true;
      delete overlay.dataset.captionKey;
      return false;
    };
    if (!value || !width || !height || !api || typeof api.isReady !== 'function' || !api.isReady()) return clear();
    const key = `${width}x${height}|${value}`;
    try {
      // Alleen opnieuw opbouwen als tekst of beeldmaat echt wijzigde.
      if (overlay.dataset.captionKey !== key) {
        overlay.dataset.captionKey = key;
        // Als <img> met een SVG-data-URL, niet als inline SVG: Chrome tekende een
        // inline SVG met filter na snel typen maar half opnieuw (Jordy 1 okt:
        // "sleel" bij "sleebos family"). Een img wordt als geheel gerasterd.
        // Pas wisselen als het nieuwe beeld gedecodeerd is (geen flits), en
        // alleen als er intussen niet verder getypt is (laatste tekst wint).
        const next = new Image();
        next.alt = '';
        next.decoding = 'async';
        next.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(api.svg(value, width, height))}`;
        const swap = () => {
          if (overlay.dataset.captionKey !== key) return;
          overlay.replaceChildren(next);
          overlay.hidden = false;
        };
        (next.decode ? next.decode() : Promise.resolve()).then(swap, swap);
        return true;
      }
    } catch (e) {
      return clear();
    }
    overlay.hidden = false;
    return true;
  };

  const FP_AI_CHECK_SVG =
    '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<circle cx="10" cy="10" r="10" fill="#2f7a4a"></circle>' +
    '<path d="M5.6 10.2l2.9 2.9 5.9-6.2" fill="none" stroke="#fff" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  customElements.define(
    'fp-ai-generator',
    class FpAiGenerator extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.dataset.endpoint;
        this.sceneId = this.dataset.sceneId;
        // Meerdere bronfoto's (gezin uit losse foto's) alleen op scenes met data-multi-photo.
        this.multiPhoto = this.dataset.multiPhoto === 'true';
        this.sourceDataUrls = null;

        // Lijst-state (zie sessionKey/saveState). generationId/previewUrl zijn
        // getters op het actieve item; nooit los toewijzen.
        this.items = [];
        this.activeId = null;
        this.remaining = null;
        this.remainingVariations = null;

        this.sourceDataUrl = null;
        this.sourcePixelUrl = null;
        this.variationIndex = 0;
        this.lastGenerationId = null;
        this.pendingSubmitId = null;
        this.pendingSubmitVariantId = null;

        this.relocated = [];
        this.attempt = null;
        this.busyTimer = null;
        this.controller = null;
        this.inFlight = false;

        this.productInfo = this.closest('product-info') || document;
        this.modal = this.querySelector('.fp-ai-modal');
        this.busyEl = this.querySelector('[data-role="busy"]');
        this.resultEl = this.querySelector('[data-role="result"]');
        this.statusEl = this.querySelector('.fp-ai-gen__status');
        this.busyText = this.querySelector('.fp-ai-gen__busy-text');
        this.barEl = this.querySelector('[data-role="bar"]');
        this.busyPhoto = this.querySelector('[data-role="busy-photo"]');
        this.busyPx = this.querySelector('[data-role="busy-px"]');
        this.busyScene = this.querySelector('[data-role="busy-scene"]');
        this.buyPanel = this.querySelector('[data-role="buy-panel"]');
        this.retryToggle = this.querySelector('[data-role="retry-toggle"]');
        this.retryPanel = this.querySelector('[data-role="retry-panel"]');
        this.stickyEl = this.querySelector('[data-role="sticky"]');
        this.stickyPrice = this.querySelector('[data-role="sticky-price"]');
        this.stickyAtc = this.querySelector('[data-role="sticky-atc"]');
        this.atcObserver = null;
        this.priceObserver = null;
        this.redoBtn = this.querySelector('.fp-ai-gen__redo');
        this.reviseChips = Array.from(this.querySelectorAll('.fp-ai-gen__revise-chip'));
        this.reviseText = this.querySelector('[data-role="revise-text"]');
        this.reviseBtn = this.querySelector('[data-role="revise-btn"]');
        this.adjustHintEl = this.querySelector('[data-role="adjust-hint"]');
        this.previewTargets = this.querySelectorAll('.fp-ai-gen__preview');
        this.initRevise();
        this.newPhotoHint = this.querySelector('[data-role="new-photo-hint"]');
        this.initMail();
        // Vóór restoreSession: die zet de bewaarde naam terug in het veld.
        this.initCaption();
        // Idem voor de naam op de voet (custom.scene_name).
        this.initSceneName();

        // Wachtschermteksten komen uit de block settings (|-gescheiden), zodat de
        // NL- en DE-markten ze via de theme-locale kunnen vertalen.
        this.busySteps = (this.dataset.textBusy || '')
          .split('|')
          .map((text) => text.trim())
          .filter(Boolean);
        this.busyStep = -1;
        this.blurStep = -1;
        this.progressStart = 0;
        this.reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

        this.triggerEl = this.querySelector('[data-role="trigger"]');
        this.doneEl = this.querySelector('[data-role="done"]');
        this.thumbsEl = this.querySelector('[data-role="thumbs"]');
        this.ctaEl = this.querySelector('[data-role="artworks-cta"]');
        this.triesEl = this.querySelector('.fp-ai-gen__tries');
        this.lightbox = this.querySelector('.fp-ai-gen__lightbox');
        this.querySelector('.fp-ai-gen__zoom').addEventListener('click', () => this.openLightbox());
        this.lightbox.addEventListener('click', () => { this.lightbox.hidden = true; });

        // De doelgroep is 55+ en die tikt op de foto, niet op het knopje ernaast.
        // Eén listener op het podium: openLightbox() pakt zelf de zichtbare view,
        // dus flat/canvas/room werken alle drie zonder aparte handlers.
        const views = this.querySelector('.fp-ai-gen__views');
        if (views) {
          views.addEventListener('click', (event) => {
            if (event.target.closest('.fp-ai-gen__zoom')) return;
            if (!event.target.closest('.fp-ai-gen__view:not([hidden]) img')) return;
            this.openLightbox();
          });
        }

        // Kaart "Your artworks": thumbs wisselen het actieve artwork, de knoppen
        // openen de modal of de cart-drawer. De + tegels zijn <label for>, die
        // hoeven geen JS (anders blokkeert de browser de file picker).
        this.doneEl.addEventListener('click', (event) => this.onCardClick(event));

        // Testmodus voor admin: hergebruikt een vaste generatie, dus geen
        // AI-tokens en geen Cloudinary-opslag tijdens het testen van de UI.
        // Een upload slaat de API-call over en speelt alleen het wachtscherm af
        // (?fp_ai_ms=6000 kort dat in voor snelle rondjes).
        //   ?fp_ai_test=1       schone PDP: uploadzone staat er, upload = hele flow
        //   ?fp_ai_test=result  springt direct naar de resultaatstaat
        // De sessie wordt in testmodus bewust niet hersteld: anders verbergt een
        // artwork uit een vorige ronde de uploadzone weer.
        const params = new URLSearchParams(window.location.search);

        // Wachtscherm-modus. 'scene' toont het persoonloze scene-artwork van dit
        // product, 'photo' de eigen foto van de klant als dat artwork ontbreekt.
        // ?fp_ai_wait= en ?fp_ai_scene= zijn er om beide te kunnen vergelijken.
        this.sceneArtwork = params.get('fp_ai_scene') || this.dataset.sceneArtwork || '';
        const wanted = params.get('fp_ai_wait');
        this.waitMode = wanted === 'photo' || !this.sceneArtwork ? 'photo' : 'scene';

        const testParam = params.get('fp_ai_test');
        this.testMode = !!(testParam && this.dataset.testPreview);
        this.testDelay = Math.max(0, parseInt(params.get('fp_ai_ms'), 10) || FP_AI_ETA_MS);
        if (this.testMode && testParam === 'result') {
          this.applyResult({
            generationId: this.dataset.testGenerationId || 'test',
            previewUrl: this.dataset.testPreview,
            remaining: null,
          });
        } else if (!this.testMode) {
          this.restoreSession();
          this.resumeFromLink(params);
        }
        this.renderArtworks();
        this.syncWithCart();

        this.inputs = this.querySelectorAll('.fp-ai-gen__input');
        this.inputs.forEach((input) => {
          input.addEventListener('change', (e) => {
            const files = Array.from((e.target && e.target.files) || []);
            if (files.length) this.handleFiles(files);
          });
        });
        this.statusAction = this.querySelector('[data-role="status-action"]');
        this.initExamples();
        this.initDropAndPaste();

        this.initReupload();
        this.redoBtn.addEventListener('click', () => { if (this.sourceDataUrl) this.generate(true); });
        // Bijsturen met feedback: chips (meerkeuze) + vrije tekst, zelfde flow als redo.
        this.reviseChips.forEach((chip) => {
          chip.addEventListener('click', () => {
            const on = chip.getAttribute('aria-pressed') !== 'true';
            chip.setAttribute('aria-pressed', String(on));
            // "Weghalen" sluit de andere knoppen uit: iemand weghalen én zijn
            // gezicht aanpassen spreekt elkaar tegen.
            if (on) {
              this.reviseChips.forEach((other) => {
                if (other !== chip && (chip.dataset.chip === 'remove' || other.dataset.chip === 'remove')) {
                  other.setAttribute('aria-pressed', 'false');
                }
              });
            }
            this.updateRedo();
          });
        });
        if (this.reviseText) this.reviseText.addEventListener('input', () => this.updateRedo());
        if (this.reviseBtn) {
          this.reviseBtn.addEventListener('click', () => {
            if (!this.sourceDataUrl || this.reviseBtn.disabled) return;
            const revision = this.revisionInput();
            this.generate(true, revision.feedback ? revision : undefined);
          });
        }
        // Is een <label for> naar de file input: hier alleen de bronfoto resetten.
        this.querySelector('.fp-ai-gen__restart').addEventListener('click', () => this.restart());
        this.querySelector('.fp-ai-modal__close').addEventListener('click', () => this.closeModal());
        // "Try again" opent de aanpassen-weergave (edit mode), geen los paneel meer.
        this.retryToggle.addEventListener('click', () => this.enterRevise());
        // Niet zelf submitten: de echte knop aanklikken houdt Dawn's handler,
        // de submit-gate en de property-injectie intact.
        this.stickyAtc.addEventListener('click', () => {
          const atc = this.buyPanel.querySelector('.product-form__submit');
          if (atc) atc.click();
        });
        // Alleen 'close': dat vuurt pas als de dialog écht dicht is, ook na
        // Escape. 'cancel' vuurt óók als de klant de bestandskiezer wegklikt,
        // en dan bleef de modal open terwijl de koopblokken al terugverhuisd
        // waren naar de PDP: prijs en Add to cart verdwenen uit beeld.
        this.modal.addEventListener('close', () => this.onModalClosed());
        // Klik op de achtergrond (buiten de modal-inhoud) sluit ook.
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.closeModal();
        });

        this.querySelectorAll('[data-view-btn]').forEach((btn) => {
          btn.addEventListener('click', () => this.switchView(btn.dataset.viewBtn));
        });

        // Afhakers die de pagina verlaten terwijl er een generatie loopt.
        // pagehide en niet beforeunload: dat laatste is onbetrouwbaar in
        // mobiele in-app browsers, en dat is ruim 90% van dit verkeer.
        // sendBeacon zodat het event de unload overleeft.
        window.addEventListener('pagehide', () =>
          this.trackAbandoned('pagehide', { transport: 'sendBeacon' })
        );

        // Submit-gate: blokkeer ATC tot er een generatie is; injecteer properties.
        this.onSubmit = (event) => {
          const form = event.target;
          // getAttribute i.p.v. form.id: Shopify-formulieren hebben <input name="id">
          // (de variant-id) en die overschaduwt de id-property van het formulier.
          if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.productFormId) return;
          if (!this.generationId) {
            event.preventDefault();
            event.stopPropagation();
            if (this.modal.open) this.showStatus(this.dataset.textGate);
            else this.showGate();
            return;
          }
          // Naam op portret: een ongeldige (of nog niet gecontroleerde) naam
          // gaat niet stilletjes verloren. Klant naar het veld, melding staat er
          // al onder. Een leeg veld blokkeert nooit.
          if (this.captionBlocked()) {
            event.preventDefault();
            event.stopPropagation();
            this.revealCaption();
            return;
          }
          // Naam op de voet: de naam in het veld wijkt af van die van het getoonde
          // beeld. Niet het oude beeld laten bestellen zonder dat de klant het
          // ziet: naar "Update preview". Zonder custom.scene_name altijd false.
          if (this.sceneNameStale()) {
            event.preventDefault();
            event.stopPropagation();
            this.revealSceneName();
            return;
          }
          // Herroepingsrecht-gate (art. 16(m)): bij een digitale variant mag de
          // order pas de deur uit met uitdrukkelijke instemming. fp-digital-consent.js
          // beheert zichtbaarheid en de properties[_withdrawal_waiver]-input zelf;
          // hier alleen blokkeren als het vinkje (nog) niet aan staat.
          if (this.isDigitalConsentBlocked(form)) {
            event.preventDefault();
            event.stopPropagation();
            this.showDigitalConsentError();
            return;
          }
          this.ensureProperty(form, 'properties[_generation_id]', this.generationId);
          this.syncPreviewProperty(form);
          this.ensureProperty(form, 'properties[_fp_subject]', this.dataset.subjectKey || '');
          this.syncCaption();
          this.syncSceneName();
          // Onthouden welk artwork er zojuist de cart in ging: de cartUpdate-payload
          // hoeft de properties niet altijd terug te geven. De variant-id erbij,
          // zodat een add-to-cart van een ander product deze vlag niet oppikt.
          this.pendingSubmitId = this.generationId;
          this.pendingSubmitVariantId = form.querySelector('[name="id"]')?.value || null;
          // Direct sluiten: Dawn's eigen handler (op het formulier zelf) draait
          // hierna en opent de drawer; die moet vrij zicht krijgen.
          this.closeModal();
        };
        document.addEventListener('submit', this.onSubmit, true);

        // fp-digital-consent.js meldt elke wissel van het herroepingsvinkje
        // (aan/uit, of variantwissel) via dit event, zodat de dynamic
        // checkout-knoppen (die niet via 'submit' lopen) meteen mee gaten.
        this.onDigitalConsentChange = () => this.updateDynamicCheckoutGate();
        document.addEventListener('fp-digital-consent:change', this.onDigitalConsentChange);

        // Dawn publiceert cartUpdate na een geslaagde add to cart
        // (assets/product-form.js r.80: { source, productVariantId, cartData }).
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        }

        // Terug van de cart met ?fp_ai_new=1: meteen naar de generator toe.
        if (params.get('fp_ai_new')) this.highlightAdd();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        window.removeEventListener('dragenter', this.onDragEnter);
        window.removeEventListener('dragover', this.onDragOver);
        window.removeEventListener('dragleave', this.onDragLeave);
        window.removeEventListener('drop', this.onDrop);
        document.removeEventListener('paste', this.onPaste);
        document.removeEventListener('fp-digital-consent:change', this.onDigitalConsentChange);
        clearTimeout(this.captionTimer);
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = null;
        }
      }

      /* ---------- foto aanleveren: voorbeelden, slepen, plakken ---------- */

      // Voorbeeldenblad (goed/fout). Heeft een eigen file input, want een
      // modale <dialog> maakt de PDP-input inert; handleFiles() sluit het blad.
      initExamples() {
        this.examplesDialog = this.querySelector('.fp-ai-examples');
        const open = this.querySelector('[data-role="examples-open"]');
        if (!this.examplesDialog || !open) return;
        open.addEventListener('click', () => {
          if (this.examplesDialog.open) return;
          this.examplesDialog.showModal();
          fpAiTrack('ai_examples_opened', { scene_id: this.sceneId });
        });
        this.querySelector('[data-role="examples-close"]')?.addEventListener('click', () => this.closeExamples());
        // Tik op de achtergrond sluit, net als bij de generator-modal.
        this.examplesDialog.addEventListener('click', (e) => {
          if (e.target === this.examplesDialog) this.closeExamples();
        });
      }

      closeExamples() {
        if (this.examplesDialog && this.examplesDialog.open) this.examplesDialog.close();
      }

      // Slepen en plakken (desktop). Zonder deze handlers opent de browser een
      // gesleepte foto zelf en is de productpagina weg. Daarom vangt de hele
      // pagina een bestand op, niet alleen het uploadblok. Een drop die een
      // ander element al afhandelde (defaultPrevented) blijft van hen.
      initDropAndPaste() {
        const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
        let depth = 0;
        const setOver = (on) => this.classList.toggle('is-dragover', on);
        this.onDragEnter = (e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          depth += 1;
          setOver(this.canAcceptPhotos());
        };
        this.onDragOver = (e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = this.canAcceptPhotos() ? 'copy' : 'none';
        };
        this.onDragLeave = (e) => {
          if (!hasFiles(e)) return;
          depth = Math.max(0, depth - 1);
          if (!depth) setOver(false);
        };
        this.onDrop = (e) => {
          if (!hasFiles(e)) return;
          depth = 0;
          setOver(false);
          if (e.defaultPrevented) return;
          e.preventDefault();
          if (!this.canAcceptPhotos()) return;
          const files = this.imageFiles(e.dataTransfer.files);
          if (files.length) this.acceptFiles(files, 'drop');
        };
        // Plakken: een foto gekopieerd uit WhatsApp Web, Facebook of Foto's.
        // Nooit kapen als de klant in een tekstveld plakt (e-mailveld, zoeken).
        this.onPaste = (e) => {
          if (!this.canAcceptPhotos()) return;
          const t = e.target;
          if (t && t.closest && t.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
          const data = e.clipboardData;
          let files = this.imageFiles(data && data.files);
          // Oudere Safari zet een geplakte afbeelding alleen in items, niet in files.
          if (!files.length && data && data.items) {
            files = this.imageFiles(
              Array.from(data.items)
                .filter((item) => item.kind === 'file')
                .map((item) => item.getAsFile())
                .filter(Boolean)
            );
          }
          if (!files.length) return;
          e.preventDefault();
          this.acceptFiles(files, 'paste');
        };
        window.addEventListener('dragenter', this.onDragEnter);
        window.addEventListener('dragover', this.onDragOver);
        window.addEventListener('dragleave', this.onDragLeave);
        window.addEventListener('drop', this.onDrop);
        document.addEventListener('paste', this.onPaste);
      }

      // Alleen als de klant ook zelf een foto zou kunnen kiezen: niet tijdens
      // een generatie en niet met de generator-modal open (daar zit "start over").
      canAcceptPhotos() {
        return !this.inFlight && !this.modal.open;
      }

      imageFiles(list) {
        return Array.from(list || []).filter(
          (file) => /^image\//.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '')
        );
      }

      // Kiezen via de fotokiezer meet fp-posthog.liquid (change-event); slepen en
      // plakken vuren geen change, dus die meten we hier, met dezelfde eventnaam.
      acceptFiles(files, source) {
        fpAiTrack('ai_photo_selected', { has_file: true, source });
        this.handleFiles(files);
      }

      /* ---------- actieve generatie ---------- */

      get activeItem() {
        if (!this.activeId) return null;
        return this.items.find((item) => item.generationId === this.activeId) || null;
      }

      get generationId() {
        const item = this.activeItem;
        return item ? item.generationId : null;
      }

      get previewUrl() {
        const item = this.activeItem;
        return item ? item.previewUrl : null;
      }

      ensureProperty(form, name, value) {
        let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }

      previewPropertyNames() {
        const names = ['properties[_fp_preview]', 'properties[Preview]'];
        const label = (this.dataset.portraitSavedLabel || '').trim();
        if (label) names.push(`properties[${label}]`);
        return names;
      }

      // Verborgen _fp_preview (de preview-URL voor cart, mails en orderflow) plus
      // een vertaalde tekstregel voor checkout en orderbevestiging. De oude
      // zichtbare 'Preview' stond als kale, niet-klikbare URL in de checkout.
      syncPreviewProperty(form) {
        this.ensureProperty(form, 'properties[_fp_preview]', this.previewUrl);
        const label = (this.dataset.portraitSavedLabel || '').trim();
        const value = (this.dataset.portraitSavedValue || '').trim();
        if (label && value) this.ensureProperty(form, `properties[${label}]`, value);
        form.querySelector('input[name="properties[Preview]"]')?.remove();
      }

      /* ---------- herroepingsrecht (digitale variant) ---------- */

      // Het consent-blok leeft los van deze component (fp-digital-consent.js
      // beheert het), maar de submit-gate hier moet weten of hij nu geldt.
      // Zoekt eerst binnen deze component (na relocateBuyBlocks staat het blok
      // in de modal), dan in productInfo — nooit document-breed, dat zou een
      // ander product op dezelfde pagina kunnen raken.
      digitalConsentEl() {
        return this.querySelector('[data-fp-digital-consent]') ||
          this.productInfo.querySelector('[data-fp-digital-consent]');
      }

      isDigitalConsentBlocked(form) {
        const el = this.digitalConsentEl();
        if (!el) return false;
        const digitalIds = (el.dataset.digitalVariants || '').split(',').map((id) => id.trim()).filter(Boolean);
        if (!digitalIds.length) return false;
        const variantId = form.querySelector('[name="id"]')?.value;
        if (!digitalIds.includes(String(variantId))) return false;
        const cb = el.querySelector('.fp-pdp-consent__cb');
        return !cb?.checked;
      }

      showDigitalConsentError() {
        const el = this.digitalConsentEl();
        if (!el) return;
        const error = el.querySelector('[data-fp-digital-consent-error]');
        if (error) error.hidden = false;
        el.querySelector('.fp-pdp-consent__cb')?.focus();
      }

      /* ---------- modal + relocatie ---------- */

      // "Add to cart" zonder preview terwijl de modal dicht is: melding bij de
      // uploadknop en die in beeld scrollen, anders gebeurt er zichtbaar niets.
      showGate() {
        const msg = this.querySelector('[data-role="gate-msg"]');
        const zone = this.querySelector('[data-role="dropzone"]');
        if (!msg || !zone) return;
        msg.textContent = this.dataset.textGate || '';
        msg.hidden = false;
        zone.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
        zone.classList.remove('is-gated');
        void zone.offsetWidth; // animatie opnieuw starten bij een tweede klik
        zone.classList.add('is-gated');
      }

      hideGate() {
        const msg = this.querySelector('[data-role="gate-msg"]');
        if (msg) msg.hidden = true;
        const zone = this.querySelector('[data-role="dropzone"]');
        if (zone) zone.classList.remove('is-gated');
      }

      openModal() {
        this.hideGate();
        // Scene-artwork vast laten laden: het wachtscherm komt er direct achter.
        if (this.waitMode === 'scene' && this.busyScene && !this.busyScene.src) {
          this.busyScene.src = this.sceneUrl();
        }
        if (!this.modal.open) {
          this.relocateBuyBlocks();
          this.modal.showModal();
          this.syncSticky();
        }
      }

      closeModal() {
        if (this.modal.open) this.modal.close();
      }

      onModalClosed() {
        // Vangnet: nooit afbreken zolang de modal nog openstaat.
        if (this.modal.open) return;
        // Vóór de abort, zodat de reden modal_close is en niet abort.
        this.trackAbandoned('modal_close');
        // Genereren onderbreken als de klant wegklikt: verzoek annuleren, geen
        // half-af generationId laten staan (anders kon je bestellen zonder preview).
        if (this.inFlight && this.controller) {
          this.controller.abort();
        }
        this.stopProgress();
        this.exitRevise();
        this.reviseCompare = null;
        this.stopStickyObservers();
        if (this.stickyEl) this.stickyEl.hidden = true;
        this.restoreBuyBlocks();
        // Wegklikken terwijl een klaar portret op de e-mail wachtte: dat portret
        // hoort gewoon in de lijst, niet kwijt.
        if (this.heldResult) this.revealResult(this.heldResult);
        // Klare resultaten blijven beschikbaar op de PDP als "Your artworks"-kaart.
        this.renderArtworks();
      }

      /* ---------- sessie-persistentie ---------- */

      sessionKey() {
        return `fpAiGen:${this.sceneId}`;
      }

      saveState() {
        try {
          sessionStorage.setItem(
            this.sessionKey(),
            JSON.stringify({
              v: FP_AI_STATE_VERSION,
              items: this.items,
              activeId: this.activeId,
              remaining: this.remaining,
              remainingVariations: this.remainingVariations,
              // Naam op portret, per scene: de ruwe invoer (komt terug in het
              // veld) en de goedgekeurde waarde (zie fpAiCaptionEvaluate).
              caption: this.captionRaw || '',
              captionValue: this.captionStatus === 'ok' ? this.captionValue : null,
            })
          );
        } catch (e) { /* private mode */ }
      }

      // De bronfoto blijft in dezelfde tab bewaard (sessionStorage, nooit
      // localStorage) zodat "Same photo, new version" en aanpassen na een
      // herlaad blijven werken. De server bewaart geen klantfoto's (privacy);
      // sessionStorage verdwijnt zodra de tab dicht gaat. Aparte sleutel, zodat
      // de hoofdstate klein blijft. Bij quota/private mode: stil overslaan.
      sourceKey() {
        return `${this.sessionKey()}:src`;
      }

      saveSource() {
        try {
          if (!this.sourceDataUrl) {
            sessionStorage.removeItem(this.sourceKey());
            return;
          }
          const urls = this.sourceDataUrls && this.sourceDataUrls.length ? this.sourceDataUrls : [this.sourceDataUrl];
          sessionStorage.setItem(this.sourceKey(), JSON.stringify({ urls, last: this.lastGenerationId || null }));
        } catch (e) {
          // Quota of private mode: liever geen bronfoto dan een verouderde.
          try { sessionStorage.removeItem(this.sourceKey()); } catch (e2) { /* ignore */ }
        }
      }

      clearSource() {
        try { sessionStorage.removeItem(this.sourceKey()); } catch (e) { /* private mode */ }
      }

      restoreSource() {
        let data = null;
        try {
          const raw = sessionStorage.getItem(this.sourceKey());
          if (!raw) return;
          data = JSON.parse(raw);
        } catch (e) { return; }
        const urls = data && Array.isArray(data.urls)
          ? data.urls.filter((u) => typeof u === 'string' && u.indexOf('data:image/') === 0)
          : [];
        if (!urls.length) return;
        this.sourceDataUrls = urls;
        this.sourceDataUrl = urls[0];
        // De foto hoort bij de generatie waarvoor hij is opgeslagen: nodig om
        // een aanpassing (feedback) op die versie te laten werken.
        if (data.last && this.items.some((i) => i.generationId === data.last)) this.lastGenerationId = data.last;
        const url = this.sourceDataUrl;
        this.pixelate(url)
          .then((px) => { if (this.sourceDataUrl === url) this.sourcePixelUrl = px; })
          .catch(() => {});
      }

      restoreSession() {
        let data = null;
        try {
          const raw = sessionStorage.getItem(this.sessionKey());
          if (!raw) return;
          data = JSON.parse(raw);
        } catch (e) { return; }
        if (!data) return;

        // Vóór de vroege return verderop (geen artworks): de naam hoort ook dan
        // terug in het veld te staan.
        this.restoreCaption(data);

        if (Array.isArray(data.items)) {
          data.items.forEach((item) => {
            if (!item || !item.generationId || !item.previewUrl) return;
            const restored = {
              generationId: item.generationId,
              previewUrl: item.previewUrl,
              inCart: !!item.inCart,
              createdAt: item.createdAt || Date.now(),
            };
            // Naam op de voet hoort bij de generatie en reist mee in de sessie.
            if (this.sceneNameEnabled && typeof item.name === 'string' && item.name) restored.name = item.name;
            this.items.push(restored);
          });
          this.remaining = typeof data.remaining === 'number' ? data.remaining : null;
          this.remainingVariations = typeof data.remainingVariations === 'number' ? data.remainingVariations : null;
          const wanted = data.activeId;
          this.activeId = this.items.some((i) => i.generationId === wanted)
            ? wanted
            : (this.items.length ? this.items[this.items.length - 1].generationId : null);
        } else if (data.generationId && data.previewUrl) {
          // v1-vorm: één generatie zonder lijst.
          this.items.push({
            generationId: data.generationId,
            previewUrl: data.previewUrl,
            inCart: false,
            createdAt: Date.now(),
          });
          this.activeId = data.generationId;
          this.remaining = typeof data.remaining === 'number' ? data.remaining : null;
          this.remainingVariations = null;
          this.saveState();
        }

        if (!this.items.length) return;
        this.restoreSource();
        this.applyActive();
        this.switchView('flat');
        this.setStage('result');
      }

      // Terug uit de preview-mail (?fp_gen=<id>&fp_img=<preview>): het portret
      // komt in de lijst en in de galerij, zodat de klant meteen een maat kiest
      // in plaats van opnieuw te uploaden. Alleen onze eigen Cloudinary-previews;
      // de order haalt het printbestand toch server-side op via _generation_id.
      resumeFromLink(params) {
        const genId = params.get('fp_gen') || '';
        let img = params.get('fp_img') || '';
        // Via /discount/SAVE10?redirect= (opvolgmails) codeert Shopify de link
        // bij het doorsturen nog een keer: dan staat er https%3A%2F%2F...
        for (let i = 0; i < 2 && /^https%3A/i.test(img); i += 1) {
          try { img = decodeURIComponent(img); } catch (e) { break; }
        }
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(genId)) return;
        if (!img.startsWith('https://res.cloudinary.com/dwlsk9oxs/')) return;
        if (!this.addItem(genId, img)) return;
        this.activeId = genId;
        this.applyActive();
        this.switchView('flat');
        this.setStage('result');
        this.saveState();
        // Uit de adresbalk halen: een gedeelde of herladen link hoeft dit niet
        // nog eens te doen.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('fp_gen');
          url.searchParams.delete('fp_img');
          history.replaceState(history.state, '', url);
        } catch (e) { /* oude browser */ }
        fpAiTrack('ai_email_resume', { scene_id: this.sceneId });
      }

      // Voegt een generatie toe (of werkt een bestaande bij) en dwingt het maximum af.
      // `name` (naam op de voet) alleen als de server er een teruggaf.
      addItem(generationId, previewUrl, name) {
        if (!generationId || !previewUrl) return null;
        const hasName = typeof name === 'string' && !!name;
        const existing = this.items.find((item) => item.generationId === generationId);
        if (existing) {
          existing.previewUrl = previewUrl;
          if (hasName) existing.name = name;
          return existing;
        }
        const item = { generationId, previewUrl, inCart: false, createdAt: Date.now() };
        if (hasName) item.name = name;
        this.items.push(item);
        while (this.items.length > FP_AI_MAX_ITEMS) {
          // Oudste dat nog niet in de cart zit valt af; staat alles in de cart,
          // dan de oudste (maar nooit de zojuist toegevoegde).
          let index = this.items.findIndex((i) => !i.inCart && i !== item);
          if (index === -1) index = this.items[0] === item ? 1 : 0;
          this.items.splice(index, 1);
        }
        return item;
      }

      setActive(generationId) {
        if (!this.items.some((item) => item.generationId === generationId)) return;
        this.activeId = generationId;
        this.applyActive();
        this.saveState();
      }

      // Zet een (herstelde of verse) generatie in de UI.
      applyResult(data) {
        // De genormaliseerde naam komt uit de response; alleen bij custom.scene_name.
        const item = this.addItem(data.generationId, data.previewUrl, this.sceneNameEnabled ? data.name : undefined);
        if (!item) {
          this.showStatus(this.dataset.textError);
          return;
        }
        this.remaining = typeof data.remaining === 'number' ? data.remaining : null;
        this.remainingVariations = typeof data.remainingVariations === 'number' ? data.remainingVariations : null;
        this.lastGenerationId = item.generationId;
        this.saveSource();
        this.activeId = item.generationId;
        // Direct na een aanpassing: "Previous version" naast de nieuwe.
        const wasRevision = !!this.reviseBase;
        this.reviseCompare =
          this.reviseBase && this.reviseBase !== item.generationId && this.items.some((i) => i.generationId === this.reviseBase)
            ? { prev: this.reviseBase, next: item.generationId }
            : null;
        this.reviseBase = null;
        if (this.newPhotoHint) this.newPhotoHint.hidden = true;
        this.applyActive();
        this.switchView('flat');
        this.setStage('result');
        // Na een geslaagde aanpassing terug naar het gewone resultaat met de nieuwe
        // versie (Jordy 22 okt: niet in de aanpassen-weergave blijven); "Previous
        // version" staat daar klaar, de link opent de weergave opnieuw.
        if (wasRevision) this.exitRevise();
      }

      // Alles wat aan het actieve artwork hangt: modal, mockups, galerij, properties.
      applyActive() {
        const item = this.activeItem;
        this.renderArtworks();
        if (!item) {
          this.removeProperties();
          this.updateCaptionGate();
          this.syncSceneName();
          this.restoreGallery();
          this.productInfo.classList?.remove('fp-ai-ready');
          return;
        }
        this.previewTargets.forEach((img) => { img.src = item.previewUrl; });
        this.setMockupImages(item.previewUrl);
        // Maatgids (fp-size-guide.js) vult de gekozen maat met dit portret.
        document.dispatchEvent(new CustomEvent('fp-ai:preview'));
        this.updateTries();
        this.updateRedo();
        this.updatePrevLink();
        this.renderMailResult();
        this.updateGallery();
        // Het naamveld toont de naam van het getoonde beeld (ook de genormaliseerde
        // versie van de server), vóór injectProperties die de gate beoordeelt.
        this.showSceneNameOf(item);
        // Properties meteen in het formulier: dynamic checkout-knoppen (Buy now,
        // Shop Pay, PayPal) serialiseren het formulier zonder submit-event.
        this.injectProperties();
        this.updateDynamicCheckoutGate();
        this.productInfo.classList?.add('fp-ai-ready');
        // Naam-overlay: tekenen zodra het beeld er is (load-handler), en
        // fp-caption alvast laden na window load.
        this.paintCaption();
        this.loadCaptionSoon();
      }

      updateTries() {
        if (!this.triesEl) return;
        const show = this.remaining !== null && this.remaining >= 0 && this.dataset.textTries;
        this.triesEl.hidden = !show;
        if (show) this.triesEl.textContent = this.dataset.textTries.replace('{count}', this.remaining);
      }

      updateRedo() {
        if (!this.redoBtn) return;
        // remainingVariations is leidend; kent de server die (nog) niet, dan valt
        // de knop terug op de dagteller voor nieuwe foto's.
        const noVariations = this.remainingVariations === 0;
        const noTries = this.remainingVariations === null && this.remaining !== null && this.remaining <= 0;
        const redoBlocked = noVariations || noTries || !this.sourceDataUrl;
        this.redoBtn.disabled = redoBlocked;
        // Zonder bronfoto (herlaad, andere tab): melding + upload-knop bovenaan,
        // chips en tekstveld op slot.
        const noPhoto = !this.sourceDataUrl;
        if (this.reuploadEl) this.reuploadEl.hidden = !noPhoto;
        if (this.reviseEl) this.reviseEl.classList.toggle('is-locked', noPhoto);
        (this.reviseChips || []).forEach((chip) => { chip.disabled = noPhoto; });
        if (this.reviseText) this.reviseText.disabled = noPhoto;
        // Zelfde blokkades als redo, plus: er moet iets te sturen zijn.
        const input = this.revisionInput();
        if (this.reviseBtn) this.reviseBtn.disabled = redoBlocked || !input.hasInput;
        // Bij "weghalen" is tikken verplicht: de tikhint zegt dat dan ook.
        if (this.adjustHintEl) {
          if (!this.adjustHintEl.dataset.textDefault) this.adjustHintEl.dataset.textDefault = this.adjustHintEl.textContent;
          const removing = input.chips.includes('remove');
          this.adjustHintEl.textContent = removing ? this.adjustHintEl.dataset.textRemove : this.adjustHintEl.dataset.textDefault;
          this.adjustHintEl.classList.toggle('is-required', removing && input.markCount === 0);
        }
      }

      // Leest chips + tekst. hasInput: minstens één chip of >= 3 tekens tekst.
      // feedback = chipinstructies (in chipvolgorde) + eventuele klantnotitie,
      // max 600 tekens (alle 6 knoppen ~330 + 150 eigen tekst). De vrije tekst gaat nooit naar PostHog (alleen de lengte).
      revisionInput() {
        const instructions = FP_AI_REVISE_INSTRUCTIONS;
        const chips = (this.reviseChips || [])
          .filter((chip) => chip.getAttribute('aria-pressed') === 'true')
          .map((chip) => chip.dataset.chip)
          .filter((key) => instructions[key]);
        const text = this.reviseText ? this.reviseText.value.replace(/\s+/g, ' ').trim() : '';
        const hasText = text.replace(/\s/g, '').length >= 3;
        const parts = chips.map((key) => instructions[key]);
        if (hasText) parts.push(`Customer note: ${text}`);
        // "Weghalen" zonder aangetikte persoon: het model weet niet wie, dus niet versturen.
        const needsMark = chips.includes('remove') && this.marks.length === 0;
        return {
          chips,
          hasText,
          textLength: hasText ? text.length : 0,
          hasInput: (chips.length > 0 || hasText) && !needsMark,
          feedback: parts.join(' ').trim().slice(0, 600),
          marks: this.marksPayload(),
          markCount: this.marks.length,
        };
      }

      clearRevision() {
        (this.reviseChips || []).forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
        if (this.reviseText) this.reviseText.value = '';
        this.marks = [];
        this.renderMarks();
        this.updateRedo();
      }

      /* ---------- aanpassen-weergave (edit mode) + markeringen ---------- */

      initRevise() {
        this.marks = [];
        this.marksFor = null;
        this.revising = false;
        this.reviseBase = null;
        this.reviseCompare = null;
        this.adjustEl = this.querySelector('[data-role="adjust"]');
        this.adjustImg = this.querySelector('[data-role="adjust-img"]');
        this.adjustStage = this.querySelector('[data-role="adjust-stage"]');
        this.marksEl = this.querySelector('[data-role="adjust-marks"]');
        this.prevBtn = this.querySelector('[data-role="revise-prev"]');
        if (!this.adjustEl || !this.adjustStage) return;

        this.querySelector('[data-role="adjust-back"]').addEventListener('click', () => this.exitRevise());
        this.querySelector('[data-role="adjust-enlarge"]').addEventListener('click', () => {
          this.openLightbox(this.adjustImg.currentSrc || this.adjustImg.src);
        });

        // Pointer events met een kleine drempel: een scrollgebaar op mobiel
        // eindigt in pointercancel of beweegt te ver en telt dus niet als tik.
        let down = null;
        this.adjustStage.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          down = { id: event.pointerId, x: event.clientX, y: event.clientY };
        });
        this.adjustStage.addEventListener('pointerup', (event) => {
          if (!down || down.id !== event.pointerId) return;
          const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
          down = null;
          if (moved <= 10) this.tapMark(event.clientX, event.clientY);
        });
        this.adjustStage.addEventListener('pointercancel', () => { down = null; });

        if (this.prevBtn) {
          // Alleen het actiedeel is klikbaar (de statustekst ervoor niet).
          this.prevBtn.addEventListener('click', (event) => {
            if (!event.target.closest('.fp-ai-gen__prev-action')) return;
            const c = this.reviseCompare;
            if (c) this.setActive(this.activeId === c.next ? c.prev : c.next);
          });
        }
      }

      enterRevise(options) {
        const item = this.activeItem;
        if (!item || !this.adjustEl) return;
        this.switchView('flat');
        // Markeringen horen bij een beeld: ander beeld, schone lei.
        if (this.marksFor !== item.generationId) {
          this.marks = [];
          this.marksFor = item.generationId;
        }
        if (this.adjustImg.getAttribute('src') !== item.previewUrl) this.adjustImg.src = item.previewUrl;
        this.revising = true;
        this.placeRevisePanel();
        this.modal.classList.add('is-revising');
        this.resultEl.classList.add('is-revising');
        this.adjustEl.hidden = false;
        this.retryPanel.hidden = false;
        this.retryToggle.setAttribute('aria-expanded', 'true');
        this.renderMarks();
        this.updateRedo();
        this.syncSticky();
        this.resetModalScroll();
        if (!options || options.track !== false) fpAiTrack('ai_revise_opened', { scene_id: this.sceneId });
      }

      // Desktop (twee kolommen): in de aanpassen-weergave staat het koopblok
      // verborgen en is de rechterkolom leeg. Het aanpassenpaneel (chips, tekst,
      // knoppen) verhuist dan naar die kolom; bij terug/sluiten komt het op
      // zijn plek in het resultaat terug (placeholder-comment als anker).
      // Zelfde breekpunt als de CSS (750px).
      placeRevisePanel() {
        const panel = this.retryPanel;
        if (!panel || !this.buyPanel) return;
        if (!this.reviseMq) {
          this.reviseMq = window.matchMedia('(min-width: 750px)');
          const onChange = () => this.placeRevisePanel();
          if (this.reviseMq.addEventListener) this.reviseMq.addEventListener('change', onChange);
          else if (this.reviseMq.addListener) this.reviseMq.addListener(onChange);
        }
        const wantSide = this.revising && this.reviseMq.matches;
        const inSide = panel.parentNode === this.buyPanel;
        if (wantSide && !inSide) {
          this.revisePanelAnchor = document.createComment('fp-ai-retry-panel');
          panel.parentNode.insertBefore(this.revisePanelAnchor, panel);
          this.buyPanel.appendChild(panel);
        } else if (!wantSide && inSide && this.revisePanelAnchor && this.revisePanelAnchor.parentNode) {
          this.revisePanelAnchor.parentNode.insertBefore(panel, this.revisePanelAnchor);
          this.revisePanelAnchor.remove();
          this.revisePanelAnchor = null;
        }
      }

      // Terug naar de gewone resultaatweergave. Chips, tekst en markeringen
      // blijven staan (ook bij een mislukte poging); clearRevision wist ze.
      exitRevise() {
        const was = this.revising;
        this.revising = false;
        this.placeRevisePanel();
        if (this.modal) this.modal.classList.remove('is-revising');
        if (this.resultEl) this.resultEl.classList.remove('is-revising');
        if (this.adjustEl) this.adjustEl.hidden = true;
        if (this.retryPanel) this.retryPanel.hidden = true;
        if (this.retryToggle) this.retryToggle.setAttribute('aria-expanded', 'false');
        if (was) {
          this.syncSticky();
          this.resetModalScroll();
        }
      }

      // Tik op het grote beeld: binnen de ring = weghalen, elders = de ene ring
      // verplaatsen (één persoon per aanpassing). Positie genormaliseerd op het zichtbare beeld (de
      // img heeft geen object-fit, de rect is dus de beeldinhoud).
      tapMark(clientX, clientY) {
        const rect = this.adjustImg.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = (clientX - rect.left) / rect.width;
        const y = (clientY - rect.top) / rect.height;
        if (x < 0 || x > 1 || y < 0 || y > 1) return;
        const radius = FP_AI_MARK_DIAMETER / 2 * rect.width;
        let hit = -1;
        let best = Infinity;
        this.marks.forEach((m, i) => {
          const d = Math.hypot((m.x - x) * rect.width, (m.y - y) * rect.height);
          if (d <= radius && d < best) { best = d; hit = i; }
        });
        if (hit >= 0) this.marks.splice(hit, 1);
        else this.marks = [...this.marks, { x, y }].slice(-FP_AI_MAX_MARKS);
        this.renderMarks();
        // "Weghalen" wacht op een tik: knop en hint volgen.
        this.updateRedo();
      }

      renderMarks() {
        if (!this.marksEl) return;
        this.marksEl.textContent = '';
        this.marks.forEach((m) => {
          const ring = document.createElement('span');
          ring.className = 'fp-ai-gen__mark';
          ring.style.left = `${(m.x * 100).toFixed(2)}%`;
          ring.style.top = `${(m.y * 100).toFixed(2)}%`;
          this.marksEl.appendChild(ring);
        });
      }

      // Voor de request: genormaliseerd 0..1, drie decimalen.
      marksPayload() {
        return this.marks.map((m) => ({ x: +m.x.toFixed(3), y: +m.y.toFixed(3) }));
      }

      // "Previous version" / "Latest version": alleen direct na een aanpassing,
      // wisselt via setActive, dus _generation_id volgt de getoonde versie.
      updatePrevLink() {
        const btn = this.prevBtn;
        if (!btn) return;
        const c = this.reviseCompare;
        const has = (id) => this.items.some((i) => i.generationId === id);
        if (!c || !has(c.prev) || !has(c.next) || (this.activeId !== c.next && this.activeId !== c.prev)) {
          btn.hidden = true;
          return;
        }
        const label = this.activeId === c.next ? btn.dataset.labelPrevious : btn.dataset.labelLatest;
        // De locale-tekst heeft de vorm "Status · Actie". Status wordt grijze
        // tekst, alleen de actie is een (onderstreepte) knop. Zonder " · " is
        // de hele tekst de actie. De elementen blijven staan en krijgen alleen
        // nieuwe tekst, zodat de focus niet verdwijnt na een klik.
        if (!this.prevParts) {
          const state = document.createElement('span');
          state.className = 'fp-ai-gen__prev-state';
          const sep = document.createElement('span');
          sep.className = 'fp-ai-gen__prev-sep';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = ' \u00b7 ';
          const action = document.createElement('button');
          action.type = 'button';
          action.className = 'fp-ai-gen__prev-action';
          btn.textContent = '';
          btn.append(state, sep, action);
          this.prevParts = { state, sep, action };
        }
        const parts = this.prevParts;
        const cut = label.indexOf(' \u00b7 ');
        const split = cut > 0 && cut + 3 < label.length;
        parts.state.textContent = split ? label.slice(0, cut) : '';
        parts.action.textContent = split ? label.slice(cut + 3) : label;
        parts.state.hidden = !split;
        parts.sep.hidden = !split;
        btn.hidden = false;
      }

      injectProperties() {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form || !this.generationId) return;
        this.ensureProperty(form, 'properties[_generation_id]', this.generationId);
        this.syncPreviewProperty(form);
        this.ensureProperty(form, 'properties[_fp_subject]', this.dataset.subjectKey || '');
        this.syncCaption();
        this.syncSceneName();
        this.updateDynamicCheckoutGate();
      }

      // Buy now / Shop Pay / PayPal-knoppen lopen niet via de submit-gate in
      // onSubmit (die luistert op het 'submit'-event van het form): ze
      // serialiseren het formulier en navigeren zelf weg. Zonder deze functie
      // zou een klant met een digitale variant en een leeg vinkje toch via
      // dynamic checkout kunnen afrekenen. Draait bij elke variant/artwork-
      // wissel (injectProperties/applyActive) én bij elke wissel van het
      // vinkje zelf, via het 'fp-digital-consent:change'-event hierboven.
      updateDynamicCheckoutGate() {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form) return;
        const blocked = this.isDigitalConsentBlocked(form);
        form.querySelectorAll('.shopify-payment-button__button').forEach((btn) => {
          btn.disabled = blocked;
          btn.setAttribute('aria-disabled', String(blocked));
        });
        const wrapper = form.querySelector('.shopify-payment-button');
        if (wrapper) wrapper.classList.toggle('fp-digital-consent-blocked', blocked);
      }

      removeProperties() {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form) return;
        [
          'properties[_generation_id]',
          ...this.previewPropertyNames(),
          'properties[_fp_subject]',
          ...this.captionPropertyNames(),
          ...this.sceneNamePropertyNames(),
        ].forEach((name) => {
          form.querySelector(`input[name="${CSS.escape(name)}"]`)?.remove();
        });
      }

      /* ---------- naam op portret ---------- */

      // Alleen op producten met custom.name_caption (data-name-caption) én als
      // fp-caption.js geladen is. Lukt dat laden niet, dan verdwijnen veld en
      // teaser en blokkeert er niets: de naam is optioneel, bestellen nooit.
      initCaption() {
        this.captionEnabled = false;
        this.captionRaw = '';
        this.captionStatus = 'empty';
        this.captionValue = null;
        this.captionReason = null;
        this.captionTrusted = null;
        this.captionTimer = null;
        this.captionLoading = null;
        this.captionLoadQueued = false;
        this.captionBox = this.querySelector('[data-role="caption"]');
        this.captionInput = this.querySelector('[data-role="caption-input"]');
        this.captionError = this.querySelector('[data-role="caption-error"]');
        this.captionOverlay = this.querySelector('[data-role="caption-overlay"]');
        this.captionTeaser = this.querySelector('[data-role="caption-teaser"]');
        this.captionWrap = this.querySelector('.fp-ai-gen__preview-wrap');
        this.captionImg = this.captionWrap ? this.captionWrap.querySelector('.fp-ai-gen__preview') : null;
        // De wrapper heeft de beeldverhouding nodig, ook als fp-caption niet laadt.
        if (this.captionImg) {
          this.captionImg.addEventListener('load', () => this.onCaptionImageLoad());
          if (this.captionImg.complete && this.captionImg.naturalWidth) this.onCaptionImageLoad();
        }
        if (this.dataset.nameCaption !== 'true' || !this.captionBox || !this.captionInput) return;
        if (!window.FPCaption || typeof window.FPCaption.ready !== 'function') {
          this.disableCaption();
          return;
        }
        this.captionEnabled = true;
        this.captionBox.hidden = false;
        if (this.captionTeaser) this.captionTeaser.hidden = false;

        this.captionInput.addEventListener('focus', () => this.loadCaption());
        this.captionInput.addEventListener('input', () => this.onCaptionInput());
        // Enter sluit op mobiel het toetsenbord; er is hier geen formulier.
        this.captionInput.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          this.captionInput.blur();
        });
        // Vaste koopbalk weg zolang het toetsenbord openstaat, net als bij het mailveld.
        this.captionBox.addEventListener('focusin', () => this.stickyEl?.classList.add('is-typing'));
        this.captionBox.addEventListener('focusout', () => this.stickyEl?.classList.remove('is-typing'));
      }

      disableCaption() {
        this.captionEnabled = false;
        if (this.captionBox) this.captionBox.hidden = true;
        if (this.captionTeaser) this.captionTeaser.hidden = true;
        this.captionStatus = 'empty';
        this.captionValue = null;
        this.captionReason = null;
        this.paintCaption();
        this.syncCaption();
      }

      restoreCaption(data) {
        if (!this.captionEnabled || typeof data.caption !== 'string' || !data.caption) return;
        const raw = data.caption.slice(0, 64);
        this.captionRaw = raw;
        this.captionInput.value = raw;
        this.captionTrusted =
          typeof data.captionValue === 'string' && data.captionValue ? { raw, value: data.captionValue } : null;
        this.updateCaption();
      }

      // opentype.js + font, eenmalig (FPCaption.ready is idempotent). Daarna
      // opnieuw beoordelen: 'pending' wordt 'ok' of 'error'.
      loadCaption() {
        if (!this.captionEnabled) return;
        if (window.FPCaption.isReady()) {
          // Al geladen (ook door een tweede generator op de pagina): alleen een
          // voorlopige status nog echt laten controleren.
          if (this.captionStatus === 'pending' || this.captionTrusted) {
            this.captionTrusted = null;
            this.updateCaption();
          }
          return;
        }
        if (this.captionLoading) return;
        this.captionLoading = window.FPCaption
          .ready(this.dataset.captionOpentypeUrl, this.dataset.captionFontUrl)
          .then(
            () => {
              this.captionLoading = null;
              this.captionTrusted = null;
              this.updateCaption();
            },
            () => {
              this.captionLoading = null;
              this.disableCaption();
            }
          );
      }

      // Zodra er een preview is: na window load alvast laden, zodat de titel er
      // direct staat als de klant typt (of meteen, bij een herstelde naam).
      // Vóór load niet, dan concurreert het met de productpagina zelf.
      loadCaptionSoon() {
        if (!this.captionEnabled) return;
        if (document.readyState === 'complete' || window.FPCaption.isReady()) {
          this.loadCaption();
        } else if (!this.captionLoadQueued) {
          this.captionLoadQueued = true;
          window.addEventListener('load', () => this.loadCaption(), { once: true });
        }
      }

      onCaptionInput() {
        if (!this.captionEnabled) return;
        this.captionRaw = this.captionInput.value;
        this.captionTrusted = null;
        // De titel staat alleen op de platte preview; typen terwijl de canvas-
        // of kamerweergave openstaat zou niets laten zien.
        const flat = this.querySelector('.fp-ai-gen__view--flat');
        if (flat && flat.hidden) this.switchView('flat');
        this.updateCaption({ debounce: true });
        // Nog niet geladen: nu wel (status is dan 'pending').
        this.loadCaption();
        if (!this.testMode) this.saveState();
      }

      // Elke toets: status, melding, properties en gate meteen (buy now leest
      // het formulier zonder submit). Alleen het tekenen wacht ~120 ms.
      updateCaption({ debounce = false } = {}) {
        const result = this.captionEnabled
          ? fpAiCaptionEvaluate(this.captionRaw, window.FPCaption, this.captionTrusted)
          : { status: 'empty', value: null, reason: null };
        this.captionStatus = result.status;
        this.captionValue = result.value;
        this.captionReason = result.reason;
        this.renderCaptionError();
        this.syncCaption();
        if (debounce && result.status === 'ok') {
          clearTimeout(this.captionTimer);
          this.captionTimer = setTimeout(() => this.paintCaption(), 120);
        } else {
          this.paintCaption();
        }
      }

      renderCaptionError() {
        if (!this.captionError || !this.captionInput) return;
        const messages = {
          too_long: this.dataset.textCaptionTooLong,
          chars: this.dataset.textCaptionChars,
          blocked: this.dataset.textCaptionBlocked,
        };
        const message = this.captionStatus === 'error' ? messages[this.captionReason] || messages.chars || '' : '';
        // Alleen bij een echte wissel schrijven: dit is een aria-live-regio.
        if (this.captionError.textContent !== message) this.captionError.textContent = message;
        if (message) this.captionInput.setAttribute('aria-invalid', 'true');
        else this.captionInput.removeAttribute('aria-invalid');
      }

      paintCaption() {
        clearTimeout(this.captionTimer);
        this.captionTimer = null;
        const value = this.captionEnabled && this.captionStatus === 'ok' ? this.captionValue : null;
        fpAiCaptionPaint(this.captionOverlay, this.captionImg, value, window.FPCaption);
      }

      onCaptionImageLoad() {
        const img = this.captionImg;
        if (!img || !img.naturalWidth || !img.naturalHeight) return;
        this.captionWrap.style.setProperty('--fp-ai-preview-ratio', `${img.naturalWidth} / ${img.naturalHeight}`);
        this.paintCaption();
      }

      captionPropertyNames() {
        const names = ['properties[_fp_caption]'];
        const label = (this.dataset.captionPropertyLabel || '').trim();
        if (label) names.push(`properties[${label}]`);
        return names;
      }

      // Verborgen _fp_caption (die leest de orderflow, en controleert hem zelf
      // opnieuw) plus dezelfde waarde onder een vertaald label voor cart,
      // checkout en orderbevestiging. Alleen bij een geldige naam én een actief
      // artwork; anders weg.
      syncCaption() {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form) return;
        const value =
          this.captionEnabled && this.captionStatus === 'ok' && this.generationId ? this.captionValue : null;
        this.captionPropertyNames().forEach((name) => {
          if (value) this.ensureProperty(form, name, value);
          else form.querySelector(`input[name="${CSS.escape(name)}"]`)?.remove();
        });
        this.updateCaptionGate(form);
      }

      captionBlocked() {
        return (
          !!this.captionEnabled &&
          !!this.generationId &&
          (this.captionStatus === 'error' || this.captionStatus === 'pending')
        );
      }

      // Eigen klasse, los van fp-digital-consent-blocked: twee gates die elkaar
      // niet uitzetten. Dynamic checkout (gesloten shadow DOM) wordt onklikbaar
      // via pointer-events en een capture-listener (toetsenbord). Add to cart
      // blijft klikbaar en oogt gedimd: de submit-gate brengt de klant naar het veld.
      updateCaptionGate(form = document.getElementById(this.dataset.productFormId)) {
        const blocked = this.captionBlocked();
        if (this.stickyAtc) this.stickyAtc.classList.toggle('fp-caption-blocked', blocked);
        if (!form) return;
        form.querySelector('.product-form__submit')?.classList.toggle('fp-caption-blocked', blocked);
        const wrapper = form.querySelector('.shopify-payment-button');
        if (!wrapper) return;
        wrapper.classList.toggle('fp-caption-blocked', blocked);
        if (wrapper.__fpCaptionGate) return;
        wrapper.__fpCaptionGate = true;
        wrapper.addEventListener(
          'click',
          (event) => {
            if (!this.captionBlocked()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.revealCaption();
          },
          true
        );
      }

      // Geblokkeerde koopklik: naar het veld, met de melding er al onder. Op de
      // PDP (modal dicht) eerst de modal openen op het resultaat.
      revealCaption() {
        if (!this.captionInput) return;
        if (!this.modal.open) {
          this.openModal();
          this.setStage('result');
        }
        this.switchView('flat');
        if (this.captionStatus === 'pending') this.loadCaption();
        // Direct, zonder frame te wachten: showModal() zet zijn eigen focus
        // synchroon, dus deze focus komt daarna en blijft staan.
        this.captionInput.focus();
      }

      /* ---------- naam op de voet (custom.scene_name) ---------- */

      // Anders dan naam op portret (een laag ná de preview) tekent de server deze
      // naam zelf in het beeld. Hij gaat dus als `name` mee in de generate-
      // aanroep en hoort daarna bij die ene generatie (item.name). Alles hier
      // is gebonden aan data-scene-name; zonder dat blijven alle methoden
      // no-ops en verandert er niets aan request, markup of properties.
      initSceneName() {
        this.sceneNameEnabled = false;
        this.sceneNameRaw = '';
        // Optionele tweede regel op het plaatje (zie sceneNameLines).
        this.sceneNameRaw2 = '';
        this.sceneNameInputs2 = [];
        this.sceneNameErrorLine = null;
        this.sceneNameError = false;
        this.sceneNameErrorReason = null;
        this.sceneNameErrorLine = null;
        // Na een geweigerde naam: knop "Update preview" ook zonder afwijking.
        this.sceneNameRetry = false;
        this.sceneNameBoxes = [];
        this.sceneNameInputs = [];
        this.sceneNameUpdateBtn = null;
        // Naamstap (tussen foto kiezen en genereren). nameStepDone: de klant
        // heeft de vraag in deze paginasessie al beantwoord, een volgende foto
        // gaat dan direct door met de naam die in het veld staat.
        this.nameStepEl = null;
        this.nameStepDone = false;
        this.nameStepPending = false;
        if (this.dataset.sceneName !== 'true') return;
        this.sceneNameBoxes = Array.from(this.querySelectorAll('[data-role="scene-name"]'));
        this.sceneNameInputs = this.sceneNameBoxes
          .map((box) => box.querySelector('[data-role="scene-name-input"]'))
          .filter(Boolean);
        this.sceneNameInputs2 = this.sceneNameBoxes
          .map((box) => box.querySelector('[data-role="scene-name-line2"]'))
          .filter(Boolean);
        this.sceneNameUpdateBtn = this.querySelector('[data-role="scene-name-update"]');
        if (!this.sceneNameInputs.length) {
          this.sceneNameBoxes = [];
          return;
        }
        this.sceneNameEnabled = true;
        const bindSceneInput = (input, line) => {
          input.addEventListener('focus', () => this.loadSceneNameCheck());
          input.addEventListener('input', () => this.onSceneNameInput(input, line));
          // Enter sluit op mobiel het toetsenbord; er is hier geen formulier.
          input.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' || event.isComposing) return;
            event.preventDefault();
            input.blur();
          });
        };
        this.sceneNameInputs.forEach((input) => bindSceneInput(input, 1));
        this.sceneNameInputs2.forEach((input) => bindSceneInput(input, 2));
        // Vaste koopbalk weg zolang het toetsenbord openstaat, net als bij het mailveld.
        this.sceneNameBoxes.forEach((box) => {
          box.addEventListener('focusin', () => this.stickyEl?.classList.add('is-typing'));
          box.addEventListener('focusout', () => this.stickyEl?.classList.remove('is-typing'));
        });
        if (this.sceneNameUpdateBtn) {
          this.sceneNameUpdateBtn.addEventListener('click', () => this.updateSceneNamePreview());
        }
        this.nameStepEl = this.querySelector('[data-role="name-step"]');
        if (this.nameStepEl) {
          this.nameStepPhoto = this.nameStepEl.querySelector('[data-role="name-step-photo"]');
          this.nameStepCount = this.nameStepEl.querySelector('[data-role="name-step-count"]');
          this.nameStepPlate = this.nameStepEl.querySelector('[data-role="name-step-plate"]');
          this.nameStepEl
            .querySelector('[data-role="name-step-go"]')
            ?.addEventListener('click', () => this.startFromNameStep());
        }
      }

      /* ---------- naamstap: foto gekozen -> naam? -> genereren ---------- */

      // Foto('s) gekozen; in plaats van direct te genereren eerst de vraag om een
      // naam. De stap staat er meteen, de foto volgt zodra hij verkleind is
      // (fillNameStepPhoto). Geen focus op het veld: het toetsenbord zou de knop
      // bedekken.
      showNameStep(count = 1) {
        this.nameStepGoQueued = false;
        this.nameStepPhoto?.removeAttribute('src');
        if (this.nameStepCount) {
          const text =
            count > 1
              ? (this.nameStepCount.dataset.textOther || '').replace('[count]', String(count))
              : this.nameStepCount.dataset.textOne || '';
          if (text) this.nameStepCount.textContent = text;
        }
        this.sceneNameError = false;
        this.sceneNameRetry = false;
        this.setStage('name');
        this.renderSceneName();
        this.loadSceneNameCheck();
        // Testrondjes (fp_ai_test) vervuilen de meting niet, net als bij de generatie-events.
        if (!this.testMode) fpAiTrack('ai_name_step_shown', { scene_id: this.dataset.sceneId, photo_count: count });
      }

      fillNameStepPhoto() {
        if (this.nameStepPhoto && this.sourceDataUrl) this.nameStepPhoto.src = this.sourceDataUrl;
      }

      // Onleesbare foto: de stap weg, de foutregel met "andere foto" blijft over.
      hideNameStep() {
        if (!this.nameStepEl) return;
        this.nameStepGoQueued = false;
        this.nameStepEl.hidden = true;
        this.modal.classList.remove('is-name-step');
      }

      // Knop in de naamstap: genereren met wat er in het veld staat (leeg mag).
      // Is de foto nog niet klaar, dan start de generatie zodra dat wel zo is.
      startFromNameStep() {
        if (this.inFlight) return;
        if (!this.sourceDataUrl) {
          this.nameStepGoQueued = true;
          return;
        }
        this.nameStepGoQueued = false;
        this.nameStepDone = true;
        this.nameStepPending = true;
        this.nameStepEl?.querySelector('[data-role="scene-name-input"]')?.blur();
        this.generate(false);
      }

      // Het messing plaatje toont wat er nu in het veld staat. De letter krimpt
      // tot de tekst tussen de schroefjes past (gemeten, niet geschat op lengte:
      // 32 brede letters zijn bijna twee keer zo breed als 32 smalle). Meten kan
      // alleen als de stap in beeld is; showNameStep rendert daarom opnieuw.
      renderNameStepPlate() {
        const plate = this.nameStepPlate;
        const label = plate && plate.firstElementChild;
        if (!label) return;
        const { l1, l2 } = this.sceneNameLines();
        const value = l1;
        const key = `${l1}\n${l2}`;
        if (label.dataset.plateKey !== key) {
          label.dataset.plateKey = key;
          label.textContent = '';
          if (!value) {
            label.textContent = plate.dataset.textEmpty || '';
          } else if (l2) {
            // Twee regels: de tweede kleiner (75%).
            const a = document.createElement('span');
            a.textContent = l1;
            const b = document.createElement('span');
            b.className = 'fp-ai-namestep__line2';
            b.textContent = l2;
            label.append(a, b);
          } else {
            label.textContent = l1;
          }
        }
        plate.classList.toggle('is-empty', !value);
        plate.classList.toggle('has-two', !!(value && l2));
        label.style.fontSize = '';
        const room = plate.clientWidth - 36;
        if (!value || room <= 0) return;
        if (l2 && label.children.length === 2) {
          // Twee regels, elk apart passend gemaakt. Regel 1 zoals altijd (20px
          // af); regel 2 mikt op 75% van de basismaat, niet kleiner dan 11px,
          // nooit groter dan regel 1 (de namen blijven het grootst) en zo nodig
          // alleen zelf kleiner (ondergrens 9px).
          const [a, b] = Array.from(label.children);
          let sizeA = 20;
          a.style.fontSize = `${sizeA}px`;
          while (sizeA > 8 && a.offsetWidth > room) {
            sizeA -= 1;
            a.style.fontSize = `${sizeA}px`;
          }
          let sizeB = Math.min(sizeA, Math.max(11, Math.round(20 * 0.75)));
          b.style.fontSize = `${sizeB}px`;
          while (sizeB > 9 && b.offsetWidth > room) {
            sizeB -= 1;
            b.style.fontSize = `${sizeB}px`;
          }
          return;
        }
        let size = 20;
        while (size > 8 && label.offsetWidth > room) {
          size -= 1;
          label.style.fontSize = `${size}px`;
        }
      }

      // Melding bij een geweigerde naam, per reden.
      sceneNameErrorText() {
        const d = this.dataset;
        const byReason = {
          too_long: d.textSceneNameTooLong,
          chars: d.textSceneNameChars,
          unsafe_chars: d.textSceneNameChars,
          blocked: d.textSceneNameBlocked,
        };
        return byReason[this.sceneNameErrorReason] || d.textSceneNameError || '';
      }

      // fp-caption.js (zelfde check als de server, incl. 32 tekens en breedte)
      // laadt opentype.js + font pas hier. Idempotent; faalt stil.
      loadSceneNameCheck() {
        const api = window.FPCaption;
        if (!this.sceneNameEnabled || !api || typeof api.ready !== 'function' || api.isReady()) return Promise.resolve();
        if (!this.sceneNameCheckLoading) {
          this.sceneNameCheckLoading = api
            .ready(this.dataset.captionOpentypeUrl, this.dataset.captionFontUrl)
            .catch(() => {})
            .then(() => { this.sceneNameCheckLoading = null; });
        }
        return this.sceneNameCheckLoading;
      }

      // Reden waarom de server deze naam zou weigeren, of null. Spiegelt
      // cleanSceneName: " \\ en regeleinden = unsafe_chars, rest via FPCaption,
      // per regel apart (een "/" binnen een regel = chars: dat is het scheidingsteken).
      // Zonder geladen FPCaption: null (de server beslist dan, zoals voorheen).
      sceneNameClientReason(value) {
        if (!value) return null;
        if (/["\\\r\n]/.test(value)) return 'unsafe_chars';
        if (value.indexOf('/') !== -1) return 'chars';
        const api = window.FPCaption;
        if (!api || typeof api.isReady !== 'function' || !api.isReady()) return null;
        const result = api.check(value);
        if (result.ok || result.reason === 'empty') return null;
        return result.reason || 'chars';
      }

      // Beide regels apart: { reason, line } of null.
      sceneNameClientCheck() {
        const { l1, l2 } = this.sceneNameLines();
        const r1 = this.sceneNameClientReason(l1);
        if (r1) return { reason: r1, line: 1 };
        // Alleen regel 2 gevuld telt als regel 1 (zie sceneNameLines).
        const r2 = this.sceneNameClientReason(l2);
        if (r2) return { reason: r2, line: 2 };
        return null;
      }

      // De twee regels, getrimd. Alleen regel 2 gevuld telt als enkele regel.
      sceneNameLines() {
        if (!this.sceneNameEnabled) return { l1: '', l2: '' };
        const a = this.sceneNameRaw.trim();
        const b = (this.sceneNameRaw2 || '').trim();
        return a ? { l1: a, l2: b } : { l1: b, l2: '' };
      }

      // Eén string, zoals de server hem verwacht: "regel 1 / regel 2" (de "/"
      // is binnen een regel niet toegestaan, dus ondubbelzinnig). Leeg = niet
      // meesturen.
      sceneNameValue() {
        const { l1, l2 } = this.sceneNameLines();
        return l2 ? `${l1} / ${l2}` : l1;
      }

      // De naam van het getoonde beeld, zoals de server hem teruggaf.
      activeSceneName() {
        const item = this.activeItem;
        return item && item.name ? item.name : '';
      }

      // Het veld wijkt af van het getoonde beeld (ook: naam toegevoegd of leeggemaakt).
      sceneNameStale() {
        return this.sceneNameEnabled && !!this.generationId && this.sceneNameValue() !== this.activeSceneName();
      }

      // Veld (beide exemplaren: PDP en modal) en state gelijk houden.
      setSceneNameRaw(value, source, line = 1) {
        if (line === 2) this.sceneNameRaw2 = value;
        else this.sceneNameRaw = value;
        (line === 2 ? this.sceneNameInputs2 : this.sceneNameInputs).forEach((input) => {
          if (input !== source && input.value !== value) input.value = value;
        });
      }

      onSceneNameInput(input, line = 1) {
        this.setSceneNameRaw(input.value, input, line);
        this.sceneNameError = false;
        this.syncSceneName();
      }

      // Een ander beeld in beeld: het veld volgt dat beeld. Een openstaande
      // wijziging of weigering hoort bij het vorige beeld en vervalt.
      showSceneNameOf(item) {
        if (!this.sceneNameEnabled) return;
        this.sceneNameError = false;
        this.sceneNameRetry = false;
        // De naam van een generatie is "regel 1 / regel 2": terug over de velden.
        const parts = item && item.name ? item.name.split(' / ') : [''];
        this.setSceneNameRaw(parts[0], null, 1);
        this.setSceneNameRaw(parts.slice(1).join(' / '), null, 2);
      }

      sceneNamePropertyNames() {
        if (!this.sceneNameEnabled) return [];
        const names = ['properties[_fp_scene_name]'];
        const label = (this.dataset.sceneNamePropertyLabel || '').trim();
        if (label) names.push(`properties[${label}]`);
        return names;
      }

      // Verborgen _fp_scene_name (leest de orderflow) plus dezelfde waarde onder
      // een vertaald label voor cart en checkout. INVARIANT: de waarde komt uit
      // de generatie (item.name, wat de server teruggaf), nooit uit het invoerveld;
      // anders kon een nog niet bijgewerkte naam op een ander beeld belanden.
      // Generatie zonder naam: geen van beide properties.
      syncSceneName() {
        if (!this.sceneNameEnabled) return;
        const form = document.getElementById(this.dataset.productFormId);
        const value = this.generationId ? this.activeSceneName() : '';
        if (form) {
          this.sceneNamePropertyNames().forEach((name) => {
            if (value) this.ensureProperty(form, name, value);
            else form.querySelector(`input[name="${CSS.escape(name)}"]`)?.remove();
          });
        }
        this.updateSceneNameGate(form);
        this.renderSceneName();
      }

      // Melding bij het veld (fout wint van "bijwerken") en de knop.
      renderSceneName() {
        if (!this.sceneNameEnabled) return;
        const stale = this.sceneNameStale();
        const message = this.sceneNameError
          ? this.sceneNameErrorText()
          : stale
            ? this.dataset.textSceneNameStale || ''
            : '';
        const errorOnly = this.sceneNameError ? message : '';
        this.sceneNameBoxes.forEach((box) => {
          const msg = box.querySelector('[data-role="scene-name-msg"]');
          const input = box.querySelector('[data-role="scene-name-input"]');
          const input2 = box.querySelector('[data-role="scene-name-line2"]');
          // In de naamstap bestaat "bijwerken" niet: daar alleen een fout.
          const text = box.classList.contains('fp-ai-gen__name--step') ? errorOnly : message;
          // Alleen bij een echte wissel schrijven: dit is een aria-live-regio.
          if (msg && msg.textContent !== text) msg.textContent = text;
          if (msg) msg.classList.toggle('is-error', this.sceneNameError);
          const line = this.sceneNameErrorLine;
          const mark = (el, on) => {
            if (!el) return;
            if (this.sceneNameError && on) el.setAttribute('aria-invalid', 'true');
            else el.removeAttribute('aria-invalid');
          };
          // Regel 2 alleen als gevuld en (bij onbekende regel) of expliciet de foute.
          const filled2 = !!(input2 && input2.value.trim());
          mark(input, line === null ? true : line === 1);
          mark(input2, line === null ? filled2 : line === 2);
        });
        if (this.sceneNameUpdateBtn) this.sceneNameUpdateBtn.hidden = !(stale || this.sceneNameRetry);
        this.renderNameStepPlate();
      }

      // Zelfde patroon als de naam-op-portret-gate, eigen klasse zodat de gates
      // elkaar niet uitzetten. Zolang de naam afwijkt is Buy now onklikbaar
      // (gesloten shadow DOM: pointer-events + capture-listener voor het
      // toetsenbord) en brengt de submit-gate een klik op Add to cart naar de
      // knop "Update preview".
      updateSceneNameGate(form = document.getElementById(this.dataset.productFormId)) {
        if (!this.sceneNameEnabled) return;
        const blocked = this.sceneNameStale();
        if (this.stickyAtc) this.stickyAtc.classList.toggle('fp-scene-name-blocked', blocked);
        if (!form) return;
        form.querySelector('.product-form__submit')?.classList.toggle('fp-scene-name-blocked', blocked);
        const wrapper = form.querySelector('.shopify-payment-button');
        if (!wrapper) return;
        wrapper.classList.toggle('fp-scene-name-blocked', blocked);
        if (wrapper.__fpSceneNameGate) return;
        wrapper.__fpSceneNameGate = true;
        wrapper.addEventListener(
          'click',
          (event) => {
            if (!this.sceneNameStale()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.revealSceneName();
          },
          true
        );
      }

      // Geblokkeerde koopklik: naar de knop, met de melding er al boven. Op de
      // PDP (modal dicht) eerst de modal openen op het resultaat.
      revealSceneName() {
        if (!this.modal.open) {
          this.openModal();
          this.setStage('result');
        }
        this.switchView('flat');
        (this.sceneNameUpdateBtn || this.sceneNameInputs[0])?.focus();
      }

      // Het modal-exemplaar staat alleen in beeld bij het resultaat (zie setStage).
      showSceneNameBox(visible) {
        const box = this.querySelector('.fp-ai-gen__name--modal');
        if (box) box.hidden = !visible;
      }

      // Server weigerde de naam (400 Invalid name). Foto en bronfoto blijven
      // staan; de klant past alleen de naam aan en tikt op "Update preview".
      // reason komt van de server (400 Invalid name) of uit de clientcheck:
      // too_long | chars | unsafe_chars | blocked | empty. Onbekend of leeg
      // valt terug op de algemene melding.
      // line: welke regel fout is (1 | 2); onbekend (server) markeert alle gevulde regels.
      onSceneNameRejected(fromNameStep = false, reason = null, source = 'server', line = null) {
        this.sceneNameErrorReason = typeof reason === 'string' ? reason : null;
        this.sceneNameErrorLine = line;
        if (!this.testMode) {
          fpAiTrack('ai_scene_name_rejected', { scene_id: this.sceneId, reason: this.sceneNameErrorReason || 'unknown', source });
        }
        if (fromNameStep && this.nameStepEl) {
          // De vraag is nog niet goed beantwoord: ook een andere foto komt
          // weer langs de naamstap.
          this.nameStepDone = false;
          this.sceneNameError = true;
          this.sceneNameRetry = false;
          this.setStage('name');
          this.renderSceneName();
          this.nameStepEl.querySelector('[data-role="scene-name-input"]')?.focus();
          return;
        }
        this.sceneNameError = true;
        this.sceneNameRetry = true;
        this.showSceneNameBox(true);
        this.renderSceneName();
        this.querySelector('.fp-ai-gen__name--modal [data-role="scene-name-input"]')?.focus();
      }

      // Nieuwe generatie met dezelfde foto('s) en de nieuwe naam: via hetzelfde
      // pad als "Try another version" (generate(true)), want dat hergebruikt
      // this.sourceDataUrl(s). Dat kan alleen als die bronfoto bij het getoonde
      // beeld hoort; anders (herladen pagina, "start over", ander artwork gekozen)
      // vraagt de modal-fotokiezer om dezelfde foto en volgt generate(false)
      // met de naam uit het veld.
      updateSceneNamePreview() {
        if (!this.sceneNameEnabled || this.inFlight) return;
        const sourceFits =
          !!this.sourceDataUrl &&
          (!this.generationId || this.generationId === this.lastGenerationId || this.sceneNameRetry);
        if (!sourceFits) {
          this.modal.querySelector('.fp-ai-gen__input')?.click();
          return;
        }
        this.generate(!!this.generationId && this.generationId === this.lastGenerationId);
      }

      /* ---------- "Your artworks"-kaart ---------- */

      onCardClick(event) {
        const thumb = event.target.closest('[data-gen-id]');
        if (thumb && this.contains(thumb)) {
          this.setActive(thumb.dataset.genId);
          this.openModal();
          this.setStage('result');
          return;
        }
        const action = event.target.closest('[data-action]');
        if (!action || !this.contains(action)) return;
        if (action.dataset.action === 'open') {
          this.openModal();
          this.setStage('result');
        } else if (action.dataset.action === 'view-cart') {
          this.openCart();
        }
      }

      openCart() {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          bubble.click();
          return;
        }
        window.location.href = this.dataset.cartUrl || '/cart';
      }

      renderArtworks() {
        const has = this.items.length > 0;
        if (this.triggerEl) this.triggerEl.hidden = has;
        if (this.doneEl) this.doneEl.hidden = !has;
        if (!this.thumbsEl || !this.ctaEl) return;
        this.thumbsEl.textContent = '';
        this.ctaEl.textContent = '';
        if (!has) return;

        this.items.forEach((item) => {
          const isActive = item.generationId === this.activeId;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'fp-ai-gen__thumb';
          btn.dataset.genId = item.generationId;
          if (isActive) btn.classList.add('is-active');
          if (item.inCart) btn.classList.add('is-in-cart');
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');

          const img = document.createElement('img');
          img.src = item.previewUrl;
          img.width = 56;
          img.height = 84;
          img.loading = 'lazy';
          img.alt = this.dataset.previewAlt || '';
          btn.appendChild(img);

          if (item.inCart) {
            const badge = document.createElement('span');
            badge.className = 'fp-ai-gen__thumb-badge';
            badge.innerHTML = FP_AI_CHECK_SVG;
            btn.appendChild(badge);
          }

          const caption = document.createElement('span');
          caption.className = 'fp-ai-gen__thumb-cap';
          if (item.inCart) caption.textContent = this.dataset.textInCart || '';
          else if (isActive) caption.textContent = this.dataset.textSelected || '';
          btn.appendChild(caption);

          this.thumbsEl.appendChild(btn);
        });

        this.thumbsEl.appendChild(this.buildAddTile());

        const active = this.activeItem;
        if (active && active.inCart) {
          // Geen losse "In your cart"-regel meer: de caption onder de thumb zegt het al.
          const make = document.createElement('label');
          make.className = 'fp-ai-gen__make button button--primary';
          make.setAttribute('for', this.dataset.inputId || '');
          make.textContent = this.dataset.textMakeAnother || '';

          const viewCart = document.createElement('button');
          viewCart.type = 'button';
          viewCart.className = 'fp-ai-gen__viewcart';
          viewCart.dataset.action = 'view-cart';
          viewCart.textContent = this.dataset.textViewCart || '';

          this.ctaEl.append(make, viewCart);
        } else {
          const open = document.createElement('button');
          open.type = 'button';
          open.className = 'fp-ai-gen__done-open button button--secondary';
          open.dataset.action = 'open';
          open.textContent = this.dataset.textDoneButton || '';
          this.ctaEl.appendChild(open);
        }
      }

      buildAddTile() {
        const add = document.createElement('label');
        add.className = 'fp-ai-gen__add';
        add.setAttribute('for', this.dataset.inputId || '');
        const plus = document.createElement('span');
        plus.className = 'fp-ai-gen__add-plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        const text = document.createElement('span');
        text.className = 'fp-ai-gen__add-txt';
        text.textContent = this.dataset.textAddAnother || '';
        add.append(plus, text);
        return add;
      }

      highlightAdd() {
        requestAnimationFrame(() => {
          this.scrollIntoView({ block: 'center' });
          const target = this.items.length
            ? this.querySelector('.fp-ai-gen__add')
            : this.querySelector('.fp-ai-gen__upload');
          if (!target) return;
          target.classList.add('fp-ai-pulse');
          setTimeout(() => target.classList.remove('fp-ai-pulse'), 2000);
        });
      }

      /* ---------- cart ---------- */

      onCartUpdate(event) {
        if (!event) return;
        // Cart-items/drawer (qty-wijziging, verwijderen) publiceren de VOLLEDIGE
        // cart (/cart/change.js): dan de vlaggen één-op-één overnemen, zodat een
        // verwijderd artwork zijn vinkje ook weer kwijtraakt.
        if (event.source !== 'product-form') {
          if (event.cartData && Array.isArray(event.cartData.items)) this.syncWithCartData(event.cartData);
          return;
        }
        const ids = this.generationIdsFromCart(event.cartData);
        let matched = false;
        this.items.forEach((item) => {
          if (ids.indexOf(item.generationId) === -1) return;
          item.inCart = true;
          matched = true;
        });
        const sameVariant =
          !this.pendingSubmitVariantId || String(event.productVariantId) === String(this.pendingSubmitVariantId);
        if (!matched && this.pendingSubmitId && sameVariant) {
          const item = this.items.find((i) => i.generationId === this.pendingSubmitId);
          if (item) {
            item.inCart = true;
            matched = true;
          }
        }
        this.pendingSubmitId = null;
        this.pendingSubmitVariantId = null;
        if (!matched) return;
        this.saveState();
        this.renderArtworks();
      }

      // Zet inCart per item op basis van een volledige cart (items[].properties).
      syncWithCartData(cartData) {
        const ids = this.generationIdsFromCart(cartData);
        let changed = false;
        this.items.forEach((item) => {
          const inCart = ids.indexOf(item.generationId) !== -1;
          if (item.inCart !== inCart) {
            item.inCart = inCart;
            changed = true;
          }
        });
        if (!changed) return;
        this.saveState();
        this.renderArtworks();
      }

      // Bij laden: de sessie-state kan achterlopen op de echte cart (verwijderd op
      // de cart-pagina, andere tab). Eén GET naar /cart.js zet het recht.
      async syncWithCart() {
        if (!this.items.length) return;
        try {
          const base = (window.routes && window.routes.cart_url) || '/cart';
          const response = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return;
          const cart = await response.json();
          if (cart && Array.isArray(cart.items)) this.syncWithCartData(cart);
        } catch (e) { /* offline/geblokkeerd: sessie-state blijft staan */ }
      }

      // /cart/add.js geeft óf het toegevoegde regelitem terug, óf { items: [...] }.
      generationIdsFromCart(cartData) {
        if (!cartData || typeof cartData !== 'object') return [];
        const lines = Array.isArray(cartData.items) ? cartData.items : [cartData];
        return lines
          .map((line) => (line && line.properties ? line.properties._generation_id : null))
          .filter(Boolean);
      }

      /* ---------- Cloudinary canvas-mockups ---------- */

      // Monteert de preview met een multiply-blend op een foto van een lege
      // canvas, zodat doektextuur en lichtval erdoorheen schijnen. Base-config:
      // "publicId|x|y|w|h" (het witte canvasvlak in de basisfoto). Afgeleiden
      // worden door Cloudinary gecachet: geen opslag- of AI-kosten.
      buildMockupUrl(previewUrl, baseConfig) {
        if (!baseConfig) return null;
        const [baseId, x, y, w, h] = baseConfig.split('|');
        if (!baseId || !w) return null;
        // Preview-URL's kunnen een q_auto/f_auto-segment bevatten; dat hoort niet
        // in het overlay-id, dus de regex slaat het over.
        const m = previewUrl.match(/^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload)(?:\/(?:f_auto|q_auto)[^/]*)?\/(?:v\d+\/)?(.+?)\.\w+$/);
        if (!m) return null;
        const overlayId = m[2].replace(/\//g, ':');
        return `${m[1]}/l_${overlayId},w_${w},h_${h},c_fill/fl_layer_apply,e_multiply,g_north_west,x_${x},y_${y}/w_1100,q_auto:eco/${baseId}.webp`;
      }

      setMockupImages(previewUrl) {
        const targets = [
          { img: '.fp-ai-gen__canvas-img', tab: '[data-view-btn="canvas"]', base: this.dataset.canvasBase },
          { img: '.fp-ai-gen__room-img', tab: '[data-view-btn="room"]', base: this.dataset.roomBase },
        ];
        targets.forEach(({ img, tab, base }) => {
          const url = this.buildMockupUrl(previewUrl, base);
          const el = this.querySelector(img);
          const btn = this.querySelector(tab);
          if (url) {
            el.src = url;
            btn.hidden = false;
          } else {
            btn.hidden = true;
          }
        });
      }

      relocateBuyBlocks() {
        if (this.relocated.length) return;
        const selectors = [
          // De wrapper (niet het inner .price-element): Dawn's variant-JS
          // vervangt de innerHTML van deze wrapper bij het wisselen van maat.
          `#price-${this.dataset.sectionId}`,
          `#variant-selects-${this.dataset.sectionId}`,
          'variant-selects',
          // Maatgids-paneel (staat buiten variant-selects, schuift mee).
          'fp-size-guide',
          // Herroepingsrecht-vinkje vóór het form: moet zichtbaar zijn vóórdat
          // de klant bij Add to cart komt (art. 16(m), zie fp-digital-consent.js).
          '[data-fp-digital-consent]',
          'product-form',
          'fp-addon',
        ];
        const seen = new Set();
        selectors.forEach((sel) => {
          this.productInfo.querySelectorAll(sel).forEach((node) => {
            if (seen.has(node) || this.contains(node)) return;
            seen.add(node);
            const placeholder = document.createComment('fp-ai-relocated');
            node.parentNode.insertBefore(placeholder, node);
            this.relocated.push({ node, placeholder, sel });
            this.buyPanel.appendChild(node);
          });
        });
      }

      // Dawn's product-info.js vervangt variant-selects bij elke maatwissel: de
      // nieuwe node komt naast de oude te staan, de oude krijgt display:none en
      // wordt na 500ms verwijderd. De node die relocateBuyBlocks verplaatste is
      // dus na een maatwissel in de modal een verouderde, onzichtbare kopie.
      // Terugzetten van die referentie gaf een PDP zonder maatknoppen (en de
      // echte, levende node bleef achter in de gesloten modal). Daarom zoeken we
      // bij het terugzetten de huidige node opnieuw op via de selector.
      liveBuyNode(entry) {
        const { node, sel } = entry;
        // Nodes die bij een ander relocated-blok horen (bv. meerdere product-form)
        // blijven buiten beschouwing.
        const claimed = new Set(this.relocated.filter((r) => r !== entry).map((r) => r.node));
        const own = Array.from(this.buyPanel.querySelectorAll(sel)).filter((n) => !claimed.has(n));
        // Eén match: dat is hem (ook als een blok bewust display:none heeft).
        // Meerdere: Dawn is midden in een vervanging, de oude kopie is verborgen.
        const visible = own.length > 1 ? own.filter((n) => n.style.display !== 'none') : own;
        let live = node;
        if (visible.length && !visible.includes(node)) live = visible[visible.length - 1];
        const stale = own.filter((n) => n !== live && n.style.display === 'none');
        return { live, stale };
      }

      restoreBuyBlocks() {
        this.relocated.forEach((entry) => {
          const { live, stale } = this.liveBuyNode(entry);
          // Verouderde kopieën (Dawn ruimt ze zelf na 500ms op, maar sluit de
          // klant eerder) mogen niet in de modal of op de PDP achterblijven.
          stale.forEach((n) => n.remove());
          if (entry.placeholder.parentNode) {
            entry.placeholder.parentNode.replaceChild(live, entry.placeholder);
          }
        });
        this.relocated = [];
      }

      // src: expliciet beeld (aanpassen-weergave); anders de zichtbare view.
      openLightbox(src) {
        const active = src ? null : this.querySelector('.fp-ai-gen__view:not([hidden]) img');
        const url = src || (active && active.src);
        if (!url) return;
        this.lightbox.querySelector('img').src = url;
        this.lightbox.hidden = false;
      }

      /* ---------- PDP-galerij overnemen ---------- */

      // Vervangt de eerste galerij-afbeelding door de actieve generatie (origineel
      // wordt bewaard en teruggezet als er geen artwork meer over is).
      updateGallery() {
        if (!this.previewUrl) return;
        const img = this.productInfo.querySelector('.product__media img');
        if (!img) return;
        if (!img.dataset.fpOriginalSrc) {
          img.dataset.fpOriginalSrc = img.src;
          img.dataset.fpOriginalSrcset = img.srcset || '';
        }
        img.srcset = '';
        img.sizes = '';

        // Dawn zet de hoogte van de galerij met --ratio op de container, afgeleid
        // van de eerste productafbeelding. Bij de AI-producten is dat een
        // vierkante mockup (1696x1696), terwijl de generatie 2:3 is. Met
        // object-fit: cover werd de preview daardoor vierkant bijgesneden.
        // Daarom de verhouding meeschalen met de generatie zelf.
        const box = img.closest('.product-media-container');
        if (box && box.dataset.fpOriginalRatio === undefined) {
          box.dataset.fpOriginalRatio = box.style.getPropertyValue('--ratio') || '';
          box.dataset.fpOriginalPreviewRatio = box.style.getPropertyValue('--preview-ratio') || '';
        }
        img.addEventListener(
          'load',
          () => {
            if (!box || !img.naturalWidth || !img.naturalHeight) return;
            const ratio = img.naturalWidth / img.naturalHeight;
            box.style.setProperty('--ratio', ratio);
            box.style.setProperty('--preview-ratio', ratio);
          },
          { once: true }
        );

        img.src = this.previewUrl;
        this.updateMediaModal(img);
      }

      // Dawn's vergrootglas opent product-media-modal, die zijn eigen kopie van
      // de productmedia bevat. Zonder deze stap krijgt de klant daar de generieke
      // productfoto te zien terwijl de galerij zijn eigen artwork toont: precies
      // op het moment dat hij de personalisatie wil controleren.
      updateMediaModal(galleryImg) {
        const modalImg = this.mediaModalImage(galleryImg);
        if (!modalImg) return;
        if (!modalImg.dataset.fpOriginalSrc) {
          modalImg.dataset.fpOriginalSrc = modalImg.src;
          modalImg.dataset.fpOriginalSrcset = modalImg.srcset || '';
        }
        modalImg.srcset = '';
        modalImg.sizes = '';
        modalImg.src = this.previewUrl;
      }

      // De modal-kopie hoort bij dezelfde media-id als de galerij-slide. Die id
      // staat op de zoom-knop van de slide; alleen de eerste slide wordt door de
      // generator vervangen, dus er is er precies één.
      //
      // Let op de zoekwortel: Dawn hangt <product-modal> buiten <product-info>,
      // als kind van <body>. Zoeken binnen productInfo levert dus niets op. Wel
      // eerst het id uit de galerij halen (dat staat wél binnen productInfo), en
      // daarna document-breed op dat id matchen: uniek genoeg om niet de
      // verkeerde PDP te raken.
      mediaModalImage(galleryImg) {
        const opener = galleryImg.closest('.product-media-container');
        const mediaId = opener?.querySelector('.product__media-toggle')?.dataset.mediaId;
        if (!mediaId) return null;
        const root = this.productInfo?.closest?.('body') || document;
        return root.querySelector(
          `.product-media-modal__content img[data-media-id="${mediaId}"]`
        );
      }

      restoreGallery() {
        const img = this.productInfo.querySelector('.product__media img');
        if (img && img.dataset.fpOriginalSrc) {
          img.src = img.dataset.fpOriginalSrc;
          img.srcset = img.dataset.fpOriginalSrcset;
          delete img.dataset.fpOriginalSrc;
          delete img.dataset.fpOriginalSrcset;
        }
        const modalImg = img && this.mediaModalImage(img);
        if (modalImg && modalImg.dataset.fpOriginalSrc) {
          modalImg.src = modalImg.dataset.fpOriginalSrc;
          modalImg.srcset = modalImg.dataset.fpOriginalSrcset;
          delete modalImg.dataset.fpOriginalSrc;
          delete modalImg.dataset.fpOriginalSrcset;
        }
        const box = img && img.closest('.product-media-container');
        if (box && box.dataset.fpOriginalRatio !== undefined) {
          ['--ratio', '--preview-ratio'].forEach((prop) => {
            const key = prop === '--ratio' ? 'fpOriginalRatio' : 'fpOriginalPreviewRatio';
            if (box.dataset[key]) box.style.setProperty(prop, box.dataset[key]);
            else box.style.removeProperty(prop);
            delete box.dataset[key];
          });
        }
      }

      /* ---------- views ---------- */

      switchView(view) {
        this.querySelectorAll('.fp-ai-gen__view').forEach((v) => {
          v.hidden = v.dataset.view !== view;
        });
        this.querySelectorAll('[data-view-btn]').forEach((b) => {
          b.classList.toggle('is-active', b.dataset.viewBtn === view);
        });
      }

      /* ---------- upload + generatie ---------- */

      // "Start over with a new photo": alleen de bronfoto en de variatieteller
      // resetten. De gemaakte artworks blijven staan; de <label> opent zelf de
      // file picker en een nieuwe upload komt er als nieuw item bij.
      restart() {
        this.sourceDataUrl = null;
        this.sourceDataUrls = null;
        this.clearSource();
        this.clearBusyPhoto();
        this.variationIndex = 0;
        this.lastGenerationId = null;
        this.clearInputs();
        this.showStatus('');
        this.updateRedo();
        if (this.newPhotoHint) this.newPhotoHint.hidden = false;
      }

      clearInputs() {
        if (!this.inputs) return;
        this.inputs.forEach((input) => { input.value = ''; });
      }

      // Eén of meer foto's. Meerdere alleen bij data-multi-photo: de server zet
      // dan iedereen uit elke foto samen in één beeld (max 4, de rest negeren).
      // De eerste foto blijft this.sourceDataUrl, zodat wachtscherm en
      // "nieuwe versie" ongewijzigd werken.
      async handleFiles(files) {
        this.showStatus('');
        this.clearInputs();
        this.closeExamples();
        this.openModal();
        this.variationIndex = 0;
        this.lastGenerationId = null;
        this.clearBusyPhoto();
        this.sourceDataUrl = null;
        this.sourceDataUrls = null;
        this.clearSource();
        const selected = this.multiPhoto ? files.slice(0, 4) : files.slice(0, 1);
        // Naam op de voet: eerst de naamstap, eenmaal per paginasessie. Zonder
        // die vlag (alle andere producten) is nameStepEl null en start de
        // generatie zoals altijd.
        const viaNameStep = this.sceneNameEnabled && !!this.nameStepEl && !this.nameStepDone;
        if (viaNameStep) this.showNameStep(selected.length);
        try {
          const urls = [];
          for (const file of selected) urls.push(await this.downscale(file));
          this.sourceDataUrls = urls;
          this.sourceDataUrl = urls[0];
          this.saveSource();
          this.sourcePixelUrl = await this.pixelate(this.sourceDataUrl).catch(() => null);
        } catch (err) {
          // Onleesbaar bestand (bv. HEIC in Chrome op desktop): andere foto kiezen.
          if (viaNameStep) this.hideNameStep();
          this.showStatus(this.dataset.textError, { retry: true });
          return;
        }
        if (viaNameStep) {
          this.fillNameStepPhoto();
          if (this.nameStepGoQueued) this.startFromNameStep();
          return;
        }
        this.generate(false);
      }

      handleFile(file) {
        return this.handleFiles([file]);
      }

      // Aanpassen-weergave zonder bronfoto: eigen verborgen file input (los van
      // de gewone uploadinputs, dus geen modus die kan blijven hangen). De foto
      // wordt alleen de nieuwe bronfoto: geen generatie, items blijven staan.
      initReupload() {
        this.reuploadEl = this.querySelector('[data-role="revise-reupload"]');
        this.reviseEl = this.querySelector('[data-role="revise"]');
        this.reuploadInput = this.querySelector('[data-role="reupload-input"]');
        const btn = this.querySelector('[data-role="reupload-btn"]');
        if (btn && this.reuploadInput) btn.addEventListener('click', () => this.reuploadInput.click());
        if (this.reuploadInput) {
          this.reuploadInput.addEventListener('change', (e) => {
            const files = Array.from((e.target && e.target.files) || []);
            e.target.value = '';
            if (files.length) this.handleReupload(files);
          });
        }
      }

      async handleReupload(files) {
        this.showStatus('');
        const selected = this.multiPhoto ? files.slice(0, 4) : files.slice(0, 1);
        try {
          const urls = [];
          for (const file of selected) urls.push(await this.downscale(file));
          this.sourceDataUrls = urls;
          this.sourceDataUrl = urls[0];
          this.sourcePixelUrl = await this.pixelate(this.sourceDataUrl).catch(() => null);
        } catch (err) {
          this.showStatus(this.dataset.textError);
          return;
        }
        // De nieuwe foto telt als bronfoto van de getoonde versie, zodat een
        // aanpassing daarop werkt (lastGenerationId was leeg na een herlaad).
        if (!this.lastGenerationId) {
          const last = this.items.length ? this.items[this.items.length - 1].generationId : null;
          this.lastGenerationId = this.activeId || last;
        }
        this.saveSource();
        this.updateRedo();
        fpAiTrack('ai_photo_reuploaded', {
          scene_id: this.sceneId,
          photo_count: this.sourceDataUrls ? this.sourceDataUrls.length : 1,
        });
      }

      async downscale(file, maxSide = 1024) {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        return canvas.toDataURL('image/jpeg', 0.85);
      }

      /* ---------- wachtscherm ---------- */

      startProgress() {
        this.busyEl.classList.remove('is-failed');
        this.progressStart = Date.now();
        this.busyStep = -1;
        this.blurStep = -1;
        this.paintBusyPhoto();
        // Terug naar 0 zonder de transitie: anders kruipt de balk bij een
        // tweede versie zichtbaar terug vanaf 100%.
        if (this.barEl) {
          this.barEl.classList.add('is-resetting');
          this.setProgress(0);
          void this.barEl.offsetWidth;
          this.barEl.classList.remove('is-resetting');
        }
        this.tickProgress();
        clearInterval(this.busyTimer);
        this.busyTimer = setInterval(() => this.tickProgress(), FP_AI_TICK_MS);
      }

      stopProgress() {
        clearInterval(this.busyTimer);
        this.busyTimer = null;
      }

      // Ease-out naar FP_AI_CAP in FP_AI_ETA_MS; daarna asymptotisch naar 99%,
      // zodat een trage generatie blijft bewegen zonder 100% te beloven.
      tickProgress() {
        const elapsed = Date.now() - this.progressStart;
        const fraction = elapsed / FP_AI_ETA_MS;
        const percent =
          fraction < 1
            ? FP_AI_CAP * (1 - Math.pow(1 - fraction, 1.6))
            : FP_AI_CAP + (99 - FP_AI_CAP) * (1 - Math.exp(-(elapsed - FP_AI_ETA_MS) / FP_AI_CRAWL_MS));
        this.setProgress(percent);
        if (!this.busySteps.length) return;
        // Stappen op verstreken tijd, niet op procenten: de balk loopt ease-out,
        // waardoor de teksten zich anders vooraan zouden opstapelen.
        const index = Math.min(this.busySteps.length - 1, Math.floor(fraction * this.busySteps.length));
        // Alleen schrijven bij een echte wissel: dit is een aria-live-regio.
        if (index === this.busyStep) return;
        this.busyStep = index;
        this.busyText.textContent = this.busySteps[index];
      }

      setProgress(percent) {
        const value = Math.max(0, Math.min(100, percent));
        this.style.setProperty('--fp-ai-progress', `${value.toFixed(1)}%`);
        if (this.barEl) this.barEl.setAttribute('aria-valuenow', String(Math.round(value)));
        if (this.reducedMotion) return;
        const steps = this.waitMode === 'scene' ? FP_AI_SCENE_BLUR_STEPS : FP_AI_BLUR_STEPS;
        const step = Math.min(steps.length - 1, Math.floor((value / 100) * steps.length));
        if (step === this.blurStep) return;
        this.blurStep = step;
        this.style.setProperty('--fp-ai-blur', `${steps[step]}px`);
      }

      // Balk dichtklappen en heel even laten staan: het eindpunt moet gezien
      // worden voordat het artwork eroverheen komt.
      async finishProgress() {
        if (!this.busyTimer) return;
        this.stopProgress();
        this.setProgress(100);
        if (this.reducedMotion) return;
        await new Promise((resolve) => setTimeout(resolve, 320));
      }

      // Shopify-CDN schaalt op de query-parameter; 600px dekt de 300px-brede
      // stage op een 2x-scherm.
      sceneUrl() {
        if (!this.sceneArtwork) return '';
        return this.sceneArtwork + (this.sceneArtwork.includes('?') ? '&' : '?') + 'width=600';
      }

      // Wat er tijdens het wachten in beeld staat.
      // scene: het persoonloze artwork van dit product, dat scherp wordt.
      // photo: de eigen foto, van mozaïek naar zacht scherp.
      paintBusyPhoto() {
        this.busyEl.classList.remove('is-revealing');
        this.busyEl.classList.toggle('is-scene', this.waitMode === 'scene');
        const steps = this.waitMode === 'scene' ? FP_AI_SCENE_BLUR_STEPS : FP_AI_BLUR_STEPS;
        this.style.setProperty('--fp-ai-blur', `${steps[this.reducedMotion ? 3 : 0]}px`);

        if (this.waitMode === 'scene') {
          if (!this.busyScene) return;
          const url = this.sceneUrl();
          [this.busyPhoto, this.busyPx].forEach((img) => { if (img) img.hidden = true; });
          if (this.busyScene.src !== url) this.busyScene.src = url;
          this.busyScene.hidden = false;
          requestAnimationFrame(() => this.busyEl.classList.add('is-revealing'));
          return;
        }

        if (!this.busyPhoto || !this.busyPx) return;
        if (this.busyScene) this.busyScene.hidden = true;
        this.busyPx.hidden = !this.sourcePixelUrl;
        if (this.sourcePixelUrl) this.busyPx.src = this.sourcePixelUrl;
        this.busyPhoto.hidden = !this.sourceDataUrl;
        if (this.sourceDataUrl) this.busyPhoto.src = this.sourceDataUrl;
        if (!this.sourceDataUrl) return;
        // Eén frame wachten, anders start de opacity-transitie niet.
        requestAnimationFrame(() => this.busyEl.classList.add('is-revealing'));
      }

      clearBusyPhoto() {
        this.sourcePixelUrl = null;
        this.busyEl.classList.remove('is-revealing');
        // Het scene-artwork blijft staan: dat is niet klantgebonden en scheelt
        // een nieuwe download bij de volgende ronde.
        [this.busyPhoto, this.busyPx].forEach((img) => {
          if (!img) return;
          img.hidden = true;
          img.removeAttribute('src');
        });
      }

      // Grof mozaïek van de bronfoto: zo begint het wachtscherm met een blokjes-
      // beeld en niet met een leeg vlak. Eén kleine canvas, geen loop.
      async pixelate(dataUrl, width = 24) {
        const img = new Image();
        img.src = dataUrl;
        await img.decode();
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * width));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/jpeg', 0.7);
      }

      setStage(stage) {
        this.busyEl.hidden = stage !== 'busy';
        this.resultEl.hidden = stage !== 'result';
        if (stage !== 'result') this.collapseRetry();
        if (this.sceneNameEnabled) this.showSceneNameBox(stage === 'result');
        if (this.nameStepEl) {
          this.nameStepEl.hidden = stage !== 'name';
          this.modal.classList.toggle('is-name-step', stage === 'name');
        }
        this.syncSticky();
        this.resetModalScroll();
      }

      /* ---------- vaste koopbalk (mobiel) ---------- */

      // De balk spiegelt de echte knop en prijs in plaats van ze na te maken:
      // zo volgt hij de vertaling, de gekozen maat en Dawn's uitverkocht-staat.
      syncSticky() {
        if (!this.stickyEl) return;
        const atc = this.buyPanel.querySelector('.product-form__submit');
        const wanted = !this.resultEl.hidden && !!atc && this.modal.open;
        if (!wanted) {
          this.stickyEl.hidden = true;
          this.stopStickyObservers();
          return;
        }
        this.stickyAtc.textContent = atc.textContent.trim();
        this.stickyAtc.disabled = atc.disabled;
        this.updateStickyPrice();
        this.stickyEl.hidden = false;
        this.watchSticky(atc);
      }

      // Dawn zet regulier, sale en uitverkocht alle drie in de DOM en verbergt
      // wat niet van toepassing is, plus visually-hidden labels. Het element dat
      // echt gerenderd wordt is dus de betrouwbaarste bron.
      updateStickyPrice() {
        if (!this.stickyPrice) return;
        const items = this.buyPanel.querySelectorAll('.price-item');
        for (const item of items) {
          const text = item.textContent.trim();
          if (text && (item.offsetWidth || item.offsetHeight)) {
            this.stickyPrice.textContent = text;
            return;
          }
        }
      }

      // Zodra de echte knop in beeld staat is de balk dubbelop, dan gaat hij weg.
      // De prijs verandert bij het wisselen van maat: Dawn vervangt dan de
      // innerHTML van de prijs-wrapper, vandaar een MutationObserver.
      watchSticky(atc) {
        this.stopStickyObservers();
        if ('IntersectionObserver' in window) {
          this.atcObserver = new IntersectionObserver(
            ([entry]) => this.stickyEl.classList.toggle('is-hidden', entry.isIntersecting),
            { threshold: 0.6 }
          );
          this.atcObserver.observe(atc);
        }
        const priceWrap = this.buyPanel.querySelector(`#price-${this.dataset.sectionId}`) || this.buyPanel.firstElementChild;
        if (priceWrap && 'MutationObserver' in window) {
          this.priceObserver = new MutationObserver(() => {
            this.updateStickyPrice();
            const live = this.buyPanel.querySelector('.product-form__submit');
            if (live) this.stickyAtc.disabled = live.disabled;
          });
          this.priceObserver.observe(priceWrap, { childList: true, subtree: true, characterData: true });
        }
      }

      stopStickyObservers() {
        if (this.atcObserver) this.atcObserver.disconnect();
        if (this.priceObserver) this.priceObserver.disconnect();
        this.atcObserver = null;
        this.priceObserver = null;
        this.stickyEl.classList.remove('is-hidden');
      }

      // Terug naar boven bij elke fase-wissel: na een (her)generatie stond de
      // klant anders nog onderaan, met het nieuwe artwork buiten beeld en op
      // iOS een stale scrollpositie waardoor de modal niet meer scrollde.
      resetModalScroll() {
        [
          this.modal,
          this.modal.querySelector('.fp-ai-modal__inner'),
          this.modal.querySelector('.fp-ai-modal__stage'),
          this.buyPanel,
        ].forEach((el) => {
          if (el) el.scrollTop = 0;
        });
      }

      collapseRetry() {
        if (!this.retryPanel) return;
        this.exitRevise();
      }

      /* ---------- preview per mail ---------- */

      // Wie een adres achterlaat, schrijft zich in (de tekst staat bij de knop
      // en gaat mee als bewijs). Het adres zelf blijft niet in de browser: de
      // server geeft een token terug waarmee volgende portretten met één tik
      // gemaild worden. localStorage en niet sessionStorage: ook morgen hoeft
      // niemand het nog eens in te typen.
      initMail() {
        this.mailWait = this.querySelector('[data-role="mail"][data-placement="wait"]');
        this.mailResult = this.querySelector('[data-role="mail"][data-placement="result"]');
        this.mailEndpoint = (this.endpoint || '').replace(/\/api\/product-generate\/?$/, '/api/preview-email');
        this.mailPending = null;
        this.heldResult = null;
        this.mailFormOpen = false;
        this.mailSending = false;
        this.mailState = this.loadMailState();
        if (!this.mailWait || !this.mailResult) {
          this.mailWait = null;
          this.mailResult = null;
          return;
        }
        [this.mailWait, this.mailResult].forEach((box) => {
          box.querySelector('[data-role="mail-form"]').addEventListener('submit', (event) => {
            event.preventDefault();
            this.onMailSubmit(box);
          });
          // Op mobiel schuift de vaste koopbalk anders over het veld zodra het
          // toetsenbord opengaat.
          box.addEventListener('focusin', () => this.stickyEl?.classList.add('is-typing'));
          box.addEventListener('focusout', () => this.stickyEl?.classList.remove('is-typing'));
        });
        this.mailWait.querySelector('[data-role="mail-skip"]').addEventListener('click', () => this.onMailSkip());
        this.mailResult.querySelector('[data-role="mail-open"]').addEventListener('click', () => this.openMailForm());
        this.mailResult.querySelector('[data-role="mail-onetap"]').addEventListener('click', () => this.onMailOneTap());
      }

      loadMailState() {
        const empty = { token: null, masked: '', skips: 0, sent: [] };
        try {
          const data = JSON.parse(localStorage.getItem('fpAiMail') || 'null');
          if (!data || typeof data !== 'object') return empty;
          return {
            token: typeof data.token === 'string' ? data.token : null,
            masked: typeof data.masked === 'string' ? data.masked : '',
            skips: Number(data.skips) || 0,
            sent: Array.isArray(data.sent) ? data.sent.slice(-20) : [],
          };
        } catch (e) {
          return empty;
        }
      }

      saveMailState() {
        try {
          localStorage.setItem('fpAiMail', JSON.stringify(this.mailState));
        } catch (e) { /* private mode */ }
      }

      mailTrack(name, props) {
        fpAiTrack(name, { scene_id: this.sceneId, ...props });
      }

      // Wachtscherm: alleen voor wie nog geen adres gaf en de vraag niet al twee
      // keer wegklikte. Stond er nog een adres klaar van een mislukte ronde, dan
      // meldt het blok dat en vraagt het niets opnieuw.
      showMailWait() {
        const box = this.mailWait;
        if (!box) return;
        const show = !!this.mailPending || (!this.mailState.token && this.mailState.skips < 2);
        box.hidden = !show;
        this.busyEl.classList.toggle('has-mail', show);
        if (!show) return;
        box.querySelector('.fp-ai-mail__submit').textContent = this.dataset.textMailSubmit;
        const skip = box.querySelector('[data-role="mail-skip"]');
        skip.textContent = this.dataset.textMailSkip;
        box.querySelector('[data-role="mail-error"]').hidden = true;
        if (this.mailPending) {
          this.showMailQueued();
          return;
        }
        box.querySelector('[data-role="mail-form"]').hidden = false;
        box.querySelector('[data-role="mail-queued"]').hidden = true;
        skip.hidden = false;
        this.mailTrack('ai_email_prompt_shown', { placement: 'wait' });
      }

      hideMailWait() {
        if (!this.mailWait) return;
        this.mailWait.hidden = true;
        this.busyEl.classList.remove('has-mail');
      }

      showMailQueued() {
        const box = this.mailWait;
        box.querySelector('[data-role="mail-form"]').hidden = true;
        box.querySelector('[data-role="mail-skip"]').hidden = true;
        box.querySelector('[data-role="mail-error"]').hidden = true;
        box.querySelector('[data-role="mail-queued"]').hidden = false;
      }

      // Klaar resultaat: direct tonen, of vasthouden als de klant nog in het
      // e-mailveld bezig is. Wegspringen midden in het typen klapt op mobiel
      // het toetsenbord dicht en gooit de invoer weg.
      finishResult(data) {
        const box = this.mailWait;
        const typing =
          box && !box.hidden && !this.mailPending &&
          (box.contains(document.activeElement) || !!box.querySelector('input[type="email"]').value.trim());
        if (!typing) {
          this.revealResult(data);
          return;
        }
        this.heldResult = data;
        // "Meestal zo'n 30 seconden" klopt niet meer als het portret er al is.
        this.busyEl.classList.add('is-ready');
        this.busyText.textContent = this.dataset.textMailReady;
        box.querySelector('.fp-ai-mail__submit').textContent = this.dataset.textMailSubmitReady;
        box.querySelector('[data-role="mail-skip"]').textContent = this.dataset.textMailView;
        this.mailTrack('ai_email_reveal_held', {});
      }

      revealResult(data) {
        this.heldResult = null;
        this.applyResult(data);
        if (!this.testMode) this.saveState();
        if (this.mailPending && this.generationId === data.generationId) {
          const pending = this.mailPending;
          this.mailPending = null;
          this.sendMail({ ...pending, placement: 'wait', generationId: data.generationId });
        }
      }

      onMailSkip() {
        const held = !!this.heldResult;
        this.mailState.skips += 1;
        this.saveMailState();
        this.mailTrack('ai_email_skipped', { placement: 'wait', held });
        this.hideMailWait();
        if (held) this.revealResult(this.heldResult);
      }

      onMailSubmit(box) {
        const placement = box.dataset.placement;
        const input = box.querySelector('input[type="email"]');
        const email = input.value.trim();
        const error = box.querySelector('[data-role="mail-error"]');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          error.textContent = this.dataset.textMailInvalid;
          error.hidden = false;
          input.focus();
          return;
        }
        error.hidden = true;
        const consentText = box.querySelector('[data-role="mail-consent"]').textContent.trim();
        this.mailTrack('ai_email_submitted', { placement, held: !!this.heldResult });
        input.blur();

        if (placement === 'result') {
          this.sendMail({ email, consentText, placement: 'result', generationId: this.generationId });
          return;
        }
        // Wachtscherm: het portret bestaat nog niet (of wacht op deze klik), dus
        // het adres gaat mee zodra er een generationId is.
        this.mailPending = { email, consentText };
        input.value = '';
        if (this.heldResult) {
          this.revealResult(this.heldResult);
        } else {
          this.showMailQueued();
        }
      }

      openMailForm() {
        const box = this.mailResult;
        this.mailFormOpen = true;
        box.querySelector('[data-role="mail-open"]').hidden = true;
        box.querySelector('[data-role="mail-open"]').setAttribute('aria-expanded', 'true');
        const form = box.querySelector('[data-role="mail-form"]');
        form.hidden = false;
        form.querySelector('input').focus();
        this.mailTrack('ai_email_prompt_shown', { placement: 'result' });
      }

      onMailOneTap() {
        if (!this.mailState.token || !this.generationId) return;
        this.mailTrack('ai_email_submitted', { placement: 'one_tap', held: false });
        this.sendMail({ token: this.mailState.token, placement: 'one_tap', generationId: this.generationId });
      }

      async sendMail({ email, token, consentText, placement, generationId }) {
        if (!generationId || this.mailSending) return;
        this.setMailSending(true);
        let result = null;
        let status = 0;
        try {
          if (this.testMode) {
            // Testmodus: geen echte inschrijving en geen mail.
            await new Promise((resolve) => setTimeout(resolve, 600));
            result = { token: 'test', maskedEmail: email ? `${email[0]}•••${email.slice(email.indexOf('@'))}` : this.mailState.masked };
            status = 200;
          } else {
            const body = {
              generationId,
              language: this.dataset.mailLang || 'en',
              placement,
              productUrl: window.location.origin + window.location.pathname,
            };
            if (token) {
              body.token = token;
            } else {
              body.email = email;
              body.consentText = consentText;
            }
            const response = await fetch(this.mailEndpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            status = response.status;
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.token) result = data;
            // Token onbekend (bijv. rij opgeruimd): opnieuw vragen, niet falen.
            if (status === 404 && data.error === 'unknown_token') {
              this.mailState.token = null;
              this.mailState.masked = '';
              this.saveMailState();
              this.setMailSending(false);
              this.renderMailResult();
              this.openMailForm();
              return;
            }
          }
        } catch (e) {
          status = 0;
        }
        this.setMailSending(false);

        if (!result) {
          this.mailTrack('ai_email_failed', { placement, status });
          this.renderMailResult();
          const box = this.mailResult;
          // Adres terug in het veld, zodat opnieuw proberen één tik is.
          if (email) {
            this.mailFormOpen = true;
            box.querySelector('[data-role="mail-open"]').hidden = true;
            box.querySelector('[data-role="mail-form"]').hidden = false;
            box.querySelector('input[type="email"]').value = email;
          }
          const error = box.querySelector('[data-role="mail-error"]');
          error.textContent = this.dataset.textMailError;
          error.hidden = false;
          return;
        }

        this.mailState.token = result.token;
        this.mailState.masked = result.maskedEmail || this.mailState.masked;
        if (!this.mailState.sent.includes(generationId)) this.mailState.sent.push(generationId);
        this.mailState.sent = this.mailState.sent.slice(-20);
        this.saveMailState();
        this.mailTrack('ai_email_sent', { placement, is_signup: !token });
        // Klaviyo on-site herkent de bezoeker nu, dus de kortingspopup blijft
        // voortaan weg bij iemand die zich net heeft ingeschreven.
        if (email && !this.testMode) {
          try { window.klaviyo?.identify?.({ email }); } catch (e) { /* geen Klaviyo */ }
        }
        const input = this.mailResult.querySelector('input[type="email"]');
        if (input) input.value = '';
        this.renderMailResult();
      }

      setMailSending(on) {
        this.mailSending = on;
        const box = this.mailResult;
        if (!box) return;
        box.querySelectorAll('button').forEach((btn) => { btn.disabled = on; });
        if (!on) return;
        box.hidden = false;
        box.querySelector('[data-role="mail-error"]').hidden = true;
        box.querySelector('[data-role="mail-open"]').hidden = true;
        box.querySelector('[data-role="mail-onetap"]').hidden = true;
        const done = box.querySelector('[data-role="mail-done"]');
        done.textContent = this.dataset.textMailSending;
        done.hidden = false;
      }

      // Onder het resultaat, per actief artwork: al gemaild (vinkje), één tik
      // voor wie al een adres gaf, of anders één rustige regel die uitklapt.
      renderMailResult() {
        const box = this.mailResult;
        if (!box || this.mailSending) return;
        const genId = this.generationId;
        box.hidden = !genId;
        if (!genId) return;
        const open = box.querySelector('[data-role="mail-open"]');
        const onetap = box.querySelector('[data-role="mail-onetap"]');
        const done = box.querySelector('[data-role="mail-done"]');
        const form = box.querySelector('[data-role="mail-form"]');
        box.querySelector('[data-role="mail-error"]').hidden = true;

        const { token, masked, sent } = this.mailState;
        const isSent = !!token && sent.includes(genId);
        done.hidden = !isSent;
        if (isSent) {
          done.innerHTML = FP_AI_CHECK_SVG;
          done.append(document.createTextNode(' ' + this.dataset.textMailSent.replace('{email}', masked)));
        }
        onetap.hidden = !token || isSent;
        if (token && !isSent) {
          onetap.querySelector('span').textContent = this.dataset.textMailOnetap.replace('{email}', masked);
        }
        if (token) {
          open.hidden = true;
          form.hidden = true;
          return;
        }
        open.hidden = this.mailFormOpen;
        form.hidden = !this.mailFormOpen;
      }

      /* ---------- metingen ---------- */

      // Per attempt_id precies één afsluitend event: completed óf abandoned.
      // De closed-vlag bewaakt dat; modal_close vuurt vóór de abort, zodat het
      // AbortError-pad daarna een no-op is.
      attemptProps() {
        return {
          attempt_id: this.attempt.id,
          scene_id: this.dataset.sceneId,
          is_redo: this.attempt.isRedo,
          photo_count: this.sourceDataUrls ? this.sourceDataUrls.length : 1,
          // Bijsturen met feedback. De vrije tekst zelf nooit meesturen.
          is_revision: !!this.attempt.revision,
          ...(this.attempt.revision
            ? {
                revision_chips: this.attempt.revision.chips,
                has_feedback_text: this.attempt.revision.hasText,
                feedback_length: this.attempt.revision.textLength,
                mark_count: this.attempt.revision.markCount || 0,
              }
            : {}),
        };
      }

      trackCompleted(limitBonus) {
        if (!this.attempt || this.attempt.closed) return;
        this.attempt.closed = true;
        fpAiTrack('ai_generation_completed', {
          ...this.attemptProps(),
          elapsed_ms: Date.now() - this.attempt.startedAt,
          // Gemaakt boven de gewone daglimiet dankzij een eerdere bestelling.
          ...(limitBonus ? { limit_bonus: true } : {}),
        });
      }

      trackAbandoned(reason, options) {
        if (!this.inFlight || !this.attempt || this.attempt.closed) return;
        this.attempt.closed = true;
        fpAiTrack('ai_generation_abandoned', {
          ...this.attemptProps(),
          elapsed_ms: Date.now() - this.attempt.startedAt,
          reason,
        }, options);
      }

      // Sluit de attempt af bij een mislukking. Zonder dit bleef de attempt_id
      // voor altijd open (geen completed, geen abandoned) en was een safety
      // block in PostHog niet te onderscheiden van een gesloten tabblad.
      trackFailed(status, reason, extra) {
        if (!this.attempt || this.attempt.closed) return;
        this.attempt.closed = true;
        fpAiTrack('ai_generation_failed', {
          ...this.attemptProps(),
          elapsed_ms: Date.now() - this.attempt.startedAt,
          status,
          reason,
          ...(extra || {}),
        });
      }

      // retry: toon "Choose another photo" onder de melding (label voor de
      // modal-input), alleen als een andere foto het probleem kan oplossen.
      showStatus(message, options = {}) {
        this.statusEl.textContent = message;
        this.statusEl.hidden = !message;
        if (this.statusAction) this.statusAction.hidden = !(message && options.retry);
      }

      // Nooit de ruwe servertekst tonen: die is Engels en technisch ("Prohibited
      // Content: ..."), ook op uniekefoto.nl en unikatportrait.de. Alleen de 503
      // (studio druk) komt al per domein vertaald van de server.
      errorFor(status, data, isVariation) {
        if (status === 422) {
          return { message: this.dataset.textBlocked || this.dataset.textError, retry: true };
        }
        if (status === 429) {
          // Een variatie-request raakt alleen de versie-limiet; een andere foto
          // kan dan nog wel. Bij nieuwe foto's zegt de server of dit IP recent
          // bestelde (buyer): kopers zitten al op de hogere limiet en horen
          // "morgen verder", de rest hoort dat een bestelling de limiet ophoogt.
          if (isVariation) {
            return { message: this.dataset.textLimitVariation || this.dataset.textLimit, retry: true };
          }
          const buyer = !!(data && data.buyer);
          return { message: (buyer && this.dataset.textLimitBuyer) || this.dataset.textLimit, retry: false };
        }
        if (status === 503 && data && data.error) return { message: data.error, retry: false };
        return { message: this.dataset.textError, retry: true };
      }

      // isVariation = "Same photo, new version": zelfde bronfoto, oplopende
      // variatie-index zodat de server pose/expressie/licht kan laten verschillen.
      // revision = { feedback, chips, hasText }: bijsturen met feedback. Alleen
      // geldig als dit een variatie is op een bestaande generatie; anders valt het
      // terug op een gewone variatie/generatie zonder feedback.
      async generate(isVariation = false, revision) {
        if (!this.sourceDataUrl) return;
        // Naam op de voet: eerst zelf controleren, zodat een weigering direct
        // bij het veld staat en niet pas na wachtscherm en e-mailvraag. Alleen
        // als er een plek is om de melding te tonen (naamstap of resultaat).
        const checkName = this.sceneNameValue();
        if (checkName && (this.nameStepPending || this.generationId)) {
          if (this.sceneNameChecking) return;
          if (window.FPCaption && typeof window.FPCaption.isReady === 'function' && !window.FPCaption.isReady()) {
            this.sceneNameChecking = true;
            try {
              await Promise.race([this.loadSceneNameCheck(), new Promise((resolve) => setTimeout(resolve, 2500))]);
            } finally {
              this.sceneNameChecking = false;
            }
          }
          const nameCheck = this.sceneNameClientCheck();
          if (nameCheck) {
            const fromStep = !!this.nameStepPending;
            this.nameStepPending = false;
            this.onSceneNameRejected(fromStep, nameCheck.reason, 'client', nameCheck.line);
            return;
          }
        }
        if (!(isVariation && revision && revision.feedback && this.lastGenerationId)) revision = null;
        // Een aanpassing werkt op het getoonde beeld, ook als dat de vorige
        // versie is (alleen binnen het nieuw/vorig-paar: zelfde bronfoto).
        const compare = this.reviseCompare;
        const revisionBase =
          revision && compare && (this.activeId === compare.next || this.activeId === compare.prev)
            ? this.activeId
            : this.lastGenerationId;
        this.reviseBase = revision ? revisionBase : null;
        this.reviseCompare = null;
        this.updatePrevLink();
        const fromNameStep = !!this.nameStepPending;
        this.nameStepPending = false;
        this.heldResult = null;
        this.busyEl.classList.remove('is-ready');
        this.setStage('busy');
        this.showStatus('');
        this.startProgress();
        this.showMailWait();

        // Testmodus: wel het echte wachtscherm, geen API-call en dus geen tokens.
        if (this.testMode) {
          await new Promise((resolve) => setTimeout(resolve, this.testDelay));
          if (!this.modal.open) {
            this.stopProgress();
            return;
          }
          await this.finishProgress();
          this.finishResult({
            generationId: this.dataset.testGenerationId || 'test',
            previewUrl: this.dataset.testPreview,
            remaining: null,
          });
          if (revision) this.clearRevision();
          return;
        }

        // Meting: start en einde koppelbaar via attempt_id. In testmodus komen
        // we hier nooit (return hierboven), dus testrondjes vervuilen niets.
        this.attempt = { id: fpAiUuid(), startedAt: Date.now(), isRedo: isVariation, closed: false, revision };
        // Naam op de voet (custom.scene_name): leeg of vlag uit = niet meesturen,
        // de body blijft dan identiek aan die van andere producten.
        const sceneName = this.sceneNameValue();
        const startProps = this.attemptProps();
        if (this.sceneNameEnabled) {
          startProps.has_name = !!sceneName;
          startProps.from_name_step = fromNameStep;
          this.sceneNameError = false;
          this.sceneNameRetry = false;
          this.renderSceneName();
        }
        fpAiTrack('ai_generation_started', startProps);

        const body = { sceneId: this.sceneId };
        if (sceneName) body.name = sceneName;
        // Meerdere foto's: `images`; anders het bestaande `image`-veld.
        if (this.sourceDataUrls && this.sourceDataUrls.length > 1) body.images = this.sourceDataUrls;
        else body.image = this.sourceDataUrl;
        if (isVariation) {
          // index = aantal eerdere variaties op deze bronfoto (eerste = 0).
          // previousGenerationId alleen meesturen als die er is: de server
          // accepteert geen null.
          body.variation = { index: this.variationIndex };
          if (this.lastGenerationId) body.variation.previousGenerationId = revision ? revisionBase : this.lastGenerationId;
          // Feedback alleen bij een echte revisie (previousGenerationId verplicht).
          if (revision) {
            body.variation.feedback = revision.feedback;
            // Optionele markeringen ("deze persoon"), alleen samen met feedback.
            if (revision.marks && revision.marks.length) body.variation.marks = revision.marks;
          }
          this.variationIndex += 1;
        } else {
          this.variationIndex = 0;
        }

        this.controller = new AbortController();
        this.inFlight = true;
        const timeout = setTimeout(() => this.controller.abort(), 90000);

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: this.controller.signal,
          });
          const data = await response.json().catch(() => ({}));

          if (!response.ok) {
            // Ongeldige naam (alleen custom.scene_name): melding bij het veld
            // in plaats van de algemene foutregel.
            const nameRejected =
              !!sceneName && response.status === 400 && !!data && data.error === 'Invalid name';
            // 422 = Gemini weigerde de foto (content-blocked). Die willen we
            // kunnen tellen: herhaalde weigeringen op hetzelfde soort foto zijn
            // een conversielek dat anders onzichtbaar blijft.
            this.trackFailed(
              response.status,
              nameRejected
                ? 'invalid_name'
                : response.status === 422
                  ? 'content_blocked'
                  : response.status === 429
                    ? 'rate_limited'
                    : 'server_error',
              response.status === 429 && !isVariation ? { buyer: !!(data && data.buyer) } : undefined
            );
            if (!nameRejected) {
              const failure = this.errorFor(response.status, data, isVariation);
              this.showStatus(failure.message, { retry: failure.retry });
            }
            // Geen portret, dus ook geen vraag om het te mailen. Een al
            // ingevuld adres blijft staan voor de volgende geslaagde ronde.
            this.hideMailWait();
            this.setStage(this.generationId ? 'result' : 'busy');
            this.busyEl.hidden = !!this.generationId;
            // Mislukte aanpassing: terug in de aanpassen-weergave, invoer staat nog.
            if (revision && this.generationId) this.enterRevise({ track: false });
            // Balk en "nog ~30 seconden" weg: het wachten is voorbij, de
            // melding eronder is nu het enige wat telt.
            this.busyEl.classList.add('is-failed');
            if (nameRejected) this.onSceneNameRejected(fromNameStep, data.reason, 'server');
            return;
          }

          await this.finishProgress();
          // Completed meet de generatie zelf, dus vóór een eventuele wachttijd
          // op het e-mailveld.
          this.trackCompleted(!!(data && data.limitBonus));
          this.finishResult(data);
          // Gelukt: chips en tekst leeg. Bij een fout blijven ze staan om opnieuw te proberen.
          if (revision) this.clearRevision();
        } catch (err) {
          // Door wegklikken geannuleerd: stil resetten, geen foutmelding.
          if (err && err.name === 'AbortError') {
            this.trackAbandoned('abort');
            if (!this.generationId) this.setStage('busy');
            return;
          }
          // Netwerkfout of timeout: status 0, want er is geen HTTP-antwoord.
          this.trackFailed(0, 'network');
          this.showStatus(this.dataset.textError, { retry: true });
          this.hideMailWait();
          this.setStage('busy');
          this.busyEl.hidden = false;
          this.busyEl.classList.add('is-failed');
        } finally {
          clearTimeout(timeout);
          this.inFlight = false;
          this.stopProgress();
        }
      }
    }
  );
}
